import { serverDetail } from "./offline/messages";
import { trackWrite } from "./offline/writes";
import { APP_BUILD, BREAK_HEADER, BUILD_HEADER, noteServerBreak } from "./release";
import { dropAccount, keepAccount as keptRow } from "./store/accounts";
import type { Reason } from "./calls";
import type { SigninEvent } from "./signinFlow";
import type { FloatSide } from "./fill";
import type { IssueReason, IssueRule, IssueStatus, Resolution, StepKind } from "./issues";
import type { FollowUpDue, FollowUpView, Missing } from "./followUps";
import type { LeadView, LostReason, NoteKind, Source, Stage } from "./leads";
import type { DealStatus, DealView, Step } from "./deals";
import type { Limits } from "./limits";
import type { SensorRules } from "./sensorRules";
import type { ReviewKind, ReviewResult } from "./review";
import type { OrderKind, OrderStatus } from "./workOrders";

export interface Bootstrap {
  name: string;
  cell: string;
  api_base: string;
  sign_in: string[];
  time_zone: string;
  identity: { api_key: string; auth_domain: string; tenant_id: string; google_client_id?: string };
}

export interface TankState {
  observed_at: string;
  measurement: { level_state?: string; fill_pct?: number };
  quality: { battery_pct?: number; flags?: string[] };
}

export interface Tank {
  id: string;
  label: string;
  label_from: "default" | "device" | "user";
  devices: string[];
  hub_id: string | null;
  state: TankState | null;
  in_field: boolean;
  can_edit: boolean;
  can_set_use: boolean;
  /** The account of the container that links the tank, if the user sees it; an older API does not send it. */
  account_id?: string | null;
}

export interface ReadingPoint {
  at: string;
  full: boolean | null;
  battery_pct: number | null;
}

export interface TankReadings {
  days: number;
  from: string;
  to: string;
  count: number;
  points: ReadingPoint[];
  before?: ReadingPoint | null;
}

export interface DeviceDetail {
  id: string;
  source: string;
  external_id: string;
  name: string | null;
  first_seen: string | null;
  last_seen: string | null;
  battery_pct: number | null;
  battery_v: number | null;
  firmware: string | null;
}

export interface TankChange {
  at: string;
  action: string;
  actor: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface TankDetails {
  devices: DeviceDetail[];
  history: TankChange[];
}

export type Flag = "near_full" | "small_container" | "rate_high" | "rate_low" | "high_scrap" | "seasonal";

export interface Projection {
  weeks_out: number | null;
  cap_weeks: number | null;
  weeks_left: number | null;
  measured_rate: number;
  rate_used: number;
  load_gal: number | null;
  load_share: number | null;
  scrap: number;
  flags: Flag[];
  averages?: number[];
  need_gal?: number | null;
  cycle_weeks?: number;
  recommended_sizes?: number[] | null;
  recommended_gal?: number | null;
  off_season?: boolean;
  season_opens?: string | null;
  rated?: boolean;
  from_restart?: boolean;
  restart?: string | null;
  /** Design 8.5: the next visit day of the area calendar, and the load on that day. */
  next_visit?: string | null;
  projected_gal?: number | null;
  projected_share?: number | null;
  early_pull?: boolean;
  early_margin?: number;
  early_risk?: number;
  estimate?: FillEstimate | null;
}

/** The 10th, 50th and 90th percentile of the fill share, and the chance that the tank is full. */
export interface FillBand {
  p10: number;
  p50: number;
  p90: number;
  risk: number;
}

/** Roadmap 063: the fill estimate from the pickups and the sensor reports. */
export interface FillEstimate {
  as_of: string | null;
  sensor: SensorType;
  float_pct: number;
  today: FillBand;
  at_visit: FillBand | null;
  anchor: { at: string | null; type: "none" | "start" | "empty" | "cross" | "level" | "driver"; fill: number };
  observations: number;
  season: { amplitude: number; peak_week: number } | null;
}

/** One day of the projection cone (rule U-24): `day` is the offset from now; null before the anchor. */
export interface ConeDay {
  day: number;
  /** The 2.5th percentile: the lower edge of the 95% band. */
  p2: number | null;
  /** The 16th percentile: the lower edge of the 68% band. */
  p16: number | null;
  p50: number | null;
  /** The 84th percentile: the upper edge of the 68% band. */
  p84: number | null;
  /** The 97.5th percentile: the upper edge of the 95% band. */
  p97: number | null;
}

/** A pickup share is the fill just before the pickup; after it the fill is 0. */
export interface ConePoint {
  at: string;
  kind: "pickup" | "driver" | "float" | "level";
  share: number;
}

export interface FillCone {
  as_of: string | null;
  capacity_gal: number;
  anchor: { at: string | null; type: string; fill: number } | null;
  next_visit?: string | null;
  days: ConeDay[];
  /** The past days with no band, estimated back from the first known fill (SO-4291); an older API sends none. */
  backcast?: ConeDay[];
  history?: ConePoint[];
  /** The days of history in the reply; an older API sends none and gives 28 days. */
  past?: number;
}

/** A time on one day of the week when the driver must not arrive. Monday is 0 (rule A-4). */
export interface Blackout {
  day: number;
  start: string;
  end: string;
}

export type Commercial = "active" | "hold" | "inactive";

export interface LatLng {
  lat: number;
  lng: number;
}

export type PointSource = "geocode" | "manual" | "seed" | "gps" | "sheet";

/** A front door or a tank pin, with who set it last. */
export interface MapPoint extends LatLng {
  source: PointSource;
  /** A rough geocode: a person must check the pin (Pins to check). */
  check?: boolean;
  geocoded_at?: string | null;
  accuracy_m?: number | null;
  set_at: string | null;
  set_by: string | null;
}

/** The tank pin of a route stop, with what an undo puts back. */
export interface StopTank extends LatLng {
  source?: PointSource | null;
  accuracy_m?: number | null;
}

export type LocationChange =
  | { point: "door" | "tank"; lat: number; lng: number; source?: "manual" | "gps" | "seed"; accuracy_m?: number | null }
  | { point: "door" | "tank"; reset: true };

export interface MapsConfig {
  browser_key: string;
  map_id: string;
}

export interface Account {
  id: string;
  number: string;
  name: string;
  address?: string;
  status: "NEW" | "REG" | "CALL" | "HOLD" | "OFF";
  commercial_status?: Commercial;
  mismatch?: string | null;
  hub_id?: string | null;
  plan: {
    area: string;
    area_id?: string;
    rotation_week: number;
    week_group: string;
    stated_rate: number;
    service_minutes?: number;
    window?: [string | null, string | null];
    scrap_exception?: boolean;
    season?: [string, string] | null;
    blackouts?: Blackout[];
    forecast_from?: string | null;
    forecast_reason?: string | null;
  } | null;
  capacity_gal: number;
  last_pickup: string | null;
  projection: Projection | null;
  can_edit?: boolean;
  deleted?: Deleted | null;
  door?: MapPoint | null;
  tank?: MapPoint | null;
  /** Rule T-5: the fields that the service or the sales sheet holds; the next import writes them again. */
  from_sheet?: string[];
  /** Rule T-9: the source of the data of the account. An older API does not send it. */
  data_source?: DataSource;
  /** Rule V-20: the pin is in the shape of another area. Absent when it is not, and from an older API. */
  outside_area?: OutsideArea;
  /** Rule W-4, only with `savings`. Null: no estimate. Absent from an older API. */
  size_saving?: SizeSaving | null;
}

/** Rule W-4: the best standard size at or above W-3, against the current size. */
export interface SizeSaving {
  best_gal: number;
  /** Real driver minutes. An older API gives the model minutes. */
  minutes_week: number;
  payback_weeks: number | null;
  overflows_week: number;
  by: "planner" | "rotation";
  /** False for a pair of sizes. Absent from an older API. */
  standard?: boolean;
  /** False when the user cannot open a work order here. Absent from an older API. */
  can_order?: boolean;
}
export interface SavingPrices {
  overflow_min: number;
  swap_min: number;
  /** The lab price of a tank in minutes, by gallons. Absent from an older API. */
  tank_min?: Record<string, number>;
}

/** The area whose shape holds the pin, and the area of the plan. `sheet`: the sheet gives the plan area. */
export interface OutsideArea {
  pin_area: string;
  pin_area_id: string;
  area: string;
  sheet: boolean;
}

export type DataSourceState = "sheet" | "app" | "scheduled";
/** Design 26.11 F4: who owns the fields of an account; `from` is the switch date of its area. */
export interface DataSource {
  state: DataSourceState;
  label: string;
  from: string | null;
}

/** When, why and by whom a lead or an account was deleted (rule U-14). */
export interface Deleted {
  at: string;
  reason: string;
  by: string | null;
}
export interface DeleteRights {
  can_delete?: boolean;
  can_purge?: boolean;
}

export type SensorType = "none" | "float" | "level";

export interface Container {
  id: string;
  kind: string;
  size_gal: number;
  tank_id: string | null;
  sensor_type: SensorType;
  float_pct: number;
}

export interface PickupLoad {
  code: string;
  approved_at: string | null;
  by_approval: boolean;
}

export interface Pickup {
  id: string;
  at: string;
  raw_gal: number;
  scrap_gal: number;
  corrects_id: string | null;
  load?: PickupLoad | null;
  /** An imported sheet week that a pickup recorded in the app replaced. */
  replaced?: boolean;
  /** A sheet pickup on or after the day its area went to the app pickups (design 26.5 T2). */
  on_app?: boolean;
}

export interface Call {
  id: string;
  at: string;
  caller: string;
  reason: Reason;
  at_fault: boolean;
  result: string;
}

export interface Issue {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  reason: IssueReason;
  source: "screen" | "call" | "review" | "sensor";
  note: string;
  status: IssueStatus;
  resolution: Resolution | null;
  due_date: string | null;
  assignee_id: string;
  assignee_email: string;
  mine: boolean;
  opened_at: string;
  updated_at: string;
  rule: IssueRule | null;
  tank_id: string | null;
  signal_at: string | null;
  can_edit: boolean;
  history: IssueHistory[];
  /** 080 C5: when the customer acknowledged the alert in the portal; an older API leaves it out. */
  customer_ack?: string | null;
}

export interface IssueHistory {
  kind: "open" | StepKind;
  to_status: IssueStatus;
  resolution: Resolution | null;
  note: string;
  at: string;
  by_email: string | null;
  assignee_email: string | null;
}

export interface IssueEvent {
  id: string;
  kind: "open" | StepKind;
  from_status: IssueStatus | null;
  to_status: IssueStatus;
  assignee_id: string | null;
  resolution: Resolution | null;
  note: string;
  at: string;
}

export interface WorkOrder {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  kind: OrderKind;
  from_size: number | null;
  to_size: number | null;
  status: OrderStatus;
  source: "screen" | "call" | "review" | "deal";
  note: string;
  opened_at: string;
  updated_at: string;
  opened_by?: string | null;
  closed_by?: string | null;
  close_note?: string;
  can_edit: boolean;
}

export interface AccountDetail extends Account, DeleteRights {
  can_record: boolean;
  can_log_call: boolean;
  can_open_issue: boolean;
  can_make_lead?: boolean;
  containers: Container[];
  pickups: Pickup[];
  pickups_total: number;
  calls: Call[] | null;
  /** Rule P-6: the newest requests of the account, or null without the view right. */
  pickup_requests?: PickupRequest[] | null;
  can_request_pickup?: boolean;
  issues: Issue[] | null;
  can_order: boolean;
  can_view_photos?: boolean;
  work_orders: WorkOrder[] | null;
  units?: DeployedUnit[] | null;
  container_sizes: number[];
  loads: LoadChoice[] | null;
  can_make_load: boolean;
  reviews?: ReviewRecord[] | null;
  contract?: Contract | null;
  contract_versions?: Contract[] | null;
  documents?: ContractDocument[] | null;
  check_ins?: CheckIn[] | null;
  can_change_contract?: boolean;
  can_reset_forecast?: boolean;
  payouts?: AccountPayout[] | null;
  can_set_door?: boolean;
  can_set_tank?: boolean;
  /** Roadmap 080: the collector may invite the restaurant to the customer portal. */
  can_invite_portal?: boolean;
  /** The open service link of the account, or null; an older API leaves it out. */
  portal_link?: { since: string } | null;
  /** The newest open invite (masked email); an older API leaves it out. */
  portal_invite?: { email: string; sent_at: string; expires_at: string; sent_by: string | null } | null;
}

export type DocumentKind = "agreement" | "cancellation" | "other";

export interface ContractDocument {
  id: string;
  kind: DocumentKind;
  file_name: string;
  size_bytes: number;
  sha256: string;
  stored: boolean;
  created_at: string;
  stored_at: string | null;
  uploaded_email: string | null;
}

export interface CheckIn {
  id: string;
  met_on: string;
  contact: string;
  /** Null for a check-in from the sales final import (roadmap 090). */
  rating: number | null;
  result: string;
  next_on: string | null;
  contract_version_id: string | null;
  recorded_email: string | null;
}

export interface Renewal {
  account_id: string;
  id: string;
  version: number;
  number: string;
  name: string;
  status: Account["status"];
  commercial_status: Commercial;
  starts_on: string;
  ends_on: string;
  term_months: number;
  auto_renew: boolean;
  notice_days: number;
  salesperson: string | null;
  last_check_in: string | null;
  next_check_in: string | null;
  days_left: number;
  window: number | null;
  mine: boolean;
  /** The last day of the notice when the contract renews automatically (rule Q-20). */
  notice_by: string | null;
  notice_passed: boolean;
}

export interface RenewalList {
  renewals: Renewal[];
  windows: number[];
  today: string;
}

/** The newest contract version. Only the roles with the contract grant get it (rule Q-14). */
export interface Contract {
  id: string;
  version: number;
  starts_on: string;
  ends_on: string;
  signed_on: string | null;
  recorded_at: string;
  rebate_method: string;
  rebate_value: number;
  consideration: string;
  consideration_amount: number;
  term_months: number;
  auto_renew: boolean;
  notice_days: number;
  payee_name: string;
  payee_address: string;
  signer_name: string;
  signer_title: string;
  salesperson: string | null;
  recorded_email?: string | null;
}

export interface ReviewRecord {
  id: string;
  kind: ReviewKind;
  result: ReviewResult;
  note: string;
  decided_at: string | null;
  decided_by: string | null;
  until: string | null;
}

export interface LoadChoice {
  id: string;
  code: string;
}

export interface LoadStop {
  pickup_id: string;
  at: string;
  raw_gal: number;
  scrap_gal: number;
  account_id: string;
  account_number: string;
  account_name: string;
  exception: boolean;
}

export interface LoadResult {
  id: string;
  raw_gal: number;
  scrap_pct: number;
  corrects_id: string | null;
  recorded_at: string;
}

export interface Load extends LoadChoice {
  day: string;
  truck: string;
  /** The capacity of the fleet truck with this code. An older API does not send it. */
  truck_gal?: number | null;
  number: number;
  mine: boolean;
  driver?: string | null;
  made_at: string;
  stops: LoadStop[];
  hidden_stops: number;
  raw_gal: number;
  scrap_gal: number;
  result: LoadResult | null;
  approval: { id: string; approved_at: string } | null;
  preview: { scrap: Record<string, number> | null; problem: string | null } | null;
  can_edit: boolean;
  can_record_result: boolean;
  can_approve: boolean;
}

export interface Me {
  email: string;
  org: { id: string; slug: string; name: string };
}

export class ApiError extends Error {
  /** The server text, also of a validation list ("raw_gal: Input should be …"), for the office and the log. */
  readonly server?: string;
  constructor(
    readonly status: number,
    readonly detail?: string,
    server?: string | null,
  ) {
    super(detail ?? `The server answered ${status}.`);
    this.server = server ?? detail;
  }
}

/** A gateway error that Hosting can give for one lost request; the API often never saw it. */
const GATEWAY = new Set([502, 503, 504]);
export const RETRY_MS = 500;
/** A POST can make a second row, so only a request that is safe to send again goes again. */
const REPEATABLE = new Set(["GET", "HEAD", "PUT", "PATCH", "DELETE"]);

/** True when the answer is a gateway error and the method is safe to send again (one more time only). */
export const retryable = (method: string | undefined, status: number) => GATEWAY.has(status) && REPEATABLE.has((method ?? "GET").toUpperCase());

/** The POST paths that only read: they do not make the kept views stale. */
const READ_POSTS = /^\/(accounts\/rows|accounts\/bulk\/preview|overview\/log|signin\/event|leads\/matches|leads\/import\/preview)$/;

export function call<T>(path: string, token?: string, init: RequestInit = {}): Promise<T> {
  const work = send<T>(path, token, init);
  return (init.method ?? "GET").toUpperCase() === "GET" ? work : trackWrite(work, !READ_POSTS.test(path.split("?")[0]));
}

async function send<T>(path: string, token: string | undefined, init: RequestInit): Promise<T> {
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (init.body) headers["Content-Type"] = "application/json";
  headers[BUILD_HEADER] = APP_BUILD;
  let response = await fetch(`/api${path}`, { ...init, headers });
  if (retryable(init.method, response.status) && !init.signal?.aborted) {
    console.info(`api: ${init.method ?? "GET"} ${path.split("?")[0]} gave ${response.status}, one more try in ${RETRY_MS} ms`);
    await new Promise((done) => setTimeout(done, RETRY_MS));
    response = await fetch(`/api${path}`, { ...init, headers });
    console.info(`api: ${init.method ?? "GET"} ${path.split("?")[0]} second try gave ${response.status}`);
  }
  noteServerBreak(response.headers?.get(BREAK_HEADER) ?? null);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, typeof body.detail === "string" ? body.detail : undefined, serverDetail(body));
  }
  return (await response.json()) as T;
}

const get = <T>(path: string, token?: string, signal?: AbortSignal) => call<T>(path, token, { signal });
/** Put the account of a write answer in the client cache. */
const kept = <T extends Account>(answer: Promise<T>) => answer.then((found) => keptRow(found));

export const bootstrap = (slug: string) => get<Bootstrap>(`/bootstrap/${encodeURIComponent(slug)}`);
export interface Discovery {
  slug: string | null;
  name: string | null;
}

const post = <T>(path: string, body: object) => call<T>(path, undefined, { method: "POST", body: JSON.stringify(body) });

export const discover = (domain: string) => get<Discovery>(`/discover/${encodeURIComponent(domain)}`);
export const signinLink = (email: string, slug?: string, connect?: string) =>
  post<{ status: string }>("/signin/link", { email, ...(slug ? { slug } : {}), ...(connect ? { connect } : {}) });
export const signinEvent = (event: SigninEvent) => post<{ status: string }>("/signin/event", event);
export const redeem = (slug: string, code: string) => post<{ custom_token: string }>("/signin/redeem", { slug, code });
export const me = (token: string) => get<Me>("/me", token);
export const tanks = async (token: string, signal?: AbortSignal) => (await get<{ tanks: Tank[] }>("/tanks", token, signal)).tanks;

/** An account of the Tanks page, with only the fields that the page shows. */
export interface ListAccount {
  id: string;
  number: string;
  name: string;
  address: string;
  status: Account["status"];
  plan: { area: string; rotation_week: number; week_group: string } | null;
  last_pickup: string | null;
  last_visit: string | null;
  projection: {
    weeks_left: number | null;
    off_season: boolean;
    next_visit: string | null;
    projected_share: number | null;
    load_share: number | null;
    today: FillBand | null;
    /** Absent from an older API: the Next visit cell then uses projected_share (rule D-14). */
    at_visit?: FillBand | null;
  } | null;
}

export interface ListContainer {
  id: string;
  account_id: string;
  kind: string;
  size_gal: number;
  sensor_type: SensorType;
  /** Absent from an older API: the app then uses 75%. */
  float_pct?: number | null;
  tank: Pick<Tank, "id" | "label" | "in_field" | "state"> | null;
}

/** A sensor tank that no container row shows; `linked` is false when no account has it. */
export type ListTank = Tank & { linked: boolean };

export interface ContainerList {
  accounts: ListAccount[];
  containers: ListContainer[];
  tanks: ListTank[];
}

export const containerList = (token: string, signal?: AbortSignal) => get<ContainerList>("/containers", token, signal);
/** No estimate: 200 with no days; an older API gives 404 "no estimate" or "Not Found". */
export const fillCone = (token: string, id: string, signal?: AbortSignal, past?: number) =>
  get<FillCone>(`/accounts/${encodeURIComponent(id)}/fill-cone${past ? `?past=${past}` : ""}`, token, signal);
export const tank = (token: string, id: string) => get<Tank>(`/tanks/${encodeURIComponent(id)}`, token);
export const tankReadings = (token: string, id: string, days: number) =>
  get<TankReadings>(`/tanks/${encodeURIComponent(id)}/readings?days=${days}`, token);
export const tankDetails = (token: string, id: string) => get<TankDetails>(`/tanks/${encodeURIComponent(id)}/details`, token);
export const renameTank = (token: string, id: string, label: string) =>
  call<Tank>(`/tanks/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify({ label }) });
export const resetTankLabel = (token: string, id: string) =>
  call<Tank>(`/tanks/${encodeURIComponent(id)}/label`, token, { method: "DELETE" });
export const setTankUse = (token: string, id: string, inField: boolean) =>
  call<Tank>(`/tanks/${encodeURIComponent(id)}/use`, token, { method: "PUT", body: JSON.stringify({ in_field: inField }) });
/** member_names: the name of each active member by email; an older API does not send it. */
export const can = (token: string) =>
  get<{ view: string[]; edit: string[]; roles: string[]; member_names?: Record<string, string>; access_key?: string }>("/me/can", token);
export const accounts = async (token: string) => (await get<{ accounts: Account[] }>("/accounts", token)).accounts;
/** This rotation week, the first day of this week, and the holidays of the 4 weeks from it. */
export interface RotationCalendar {
  this_week: number;
  week_start: string;
  holidays?: string[];
}
export const accountList = (token: string, deleted = false, signal?: AbortSignal) =>
  get<{ accounts: Account[]; can_delete?: boolean; can_bulk_edit?: boolean; rotation?: RotationCalendar; as_of?: number }>(deleted ? "/accounts?deleted=true" : "/accounts", token, signal);
/** The accounts with the size saving of each (rule W-4). An older API sends no savings. */
export const sizeAccounts = (token: string, signal?: AbortSignal) =>
  get<{ accounts: Account[]; saving_prices?: SavingPrices }>("/accounts?savings=true", token, signal);

/** One change feed event: the ids of one entity in one seq, or null ids for a resync (design 6.10). */
export interface ChangeEvent {
  seq: number;
  entity: string;
  ids: string[] | null;
}

export interface ChangeFeed {
  head: number;
  access_key: string;
  events: ChangeEvent[];
  resync: string[];
  more: boolean;
  reason?: string | null;
}

export const changes = (token: string, since: number, signal?: AbortSignal) => get<ChangeFeed>(`/changes?since=${since}`, token, signal);
/** The list rows of at most 500 accounts; `gone` holds the ids that are deleted or not visible. */
export const accountRows = (token: string, ids: string[], signal?: AbortSignal) =>
  call<{ accounts: Account[]; gone: string[]; as_of: number }>("/accounts/rows", token, { method: "POST", body: JSON.stringify({ ids }), signal });

export type BulkField = "status" | "week_group" | "window" | "season" | "blackouts";

/** Only the fields in the body change. Both window or season days null removes them (rule A-6). */
export interface BulkChanges {
  status?: Account["status"];
  week_group?: string;
  window_start?: string | null;
  window_end?: string | null;
  season_start?: string | null;
  season_end?: string | null;
  blackouts?: Blackout[];
}

export interface BulkRow {
  account_id: string;
  number: string | null;
  name: string | null;
  before: Partial<Record<BulkField, unknown>>;
  after: Partial<Record<BulkField, unknown>>;
  changed: boolean;
  refused: string | null;
}

export interface BulkResult {
  change_set_id?: string;
  fields: BulkField[];
  rows: BulkRow[];
  /** Rule T-5: the picked accounts from the sheet, and the changed fields that the sheet holds. */
  sheet?: { accounts: number; fields: BulkField[] };
}

const bulkBody = (ids: readonly string[], changes: BulkChanges) => JSON.stringify({ account_ids: ids, changes });
export const previewBulk = (token: string, ids: readonly string[], changes: BulkChanges) =>
  call<BulkResult>("/accounts/bulk/preview", token, { method: "POST", body: bulkBody(ids, changes) });
export const applyBulk = (token: string, ids: readonly string[], changes: BulkChanges) =>
  call<BulkResult>("/accounts/bulk", token, { method: "POST", body: bulkBody(ids, changes) });
/** Each geocoded front door becomes a pin placed by hand; the other doors stay (roadmap 020). */
export interface PinResult {
  change_set_id: string;
  confirmed: number;
  placed: number;
}

export const confirmPins = (token: string, ids: readonly string[]) =>
  call<PinResult>("/accounts/bulk/pins", token, { method: "POST", body: JSON.stringify({ account_ids: ids }) });
export const undoBulk = (token: string, id: string) =>
  call<{ change_set_id: string; undone: number }>(`/change-sets/${encodeURIComponent(id)}/undo`, token, { method: "POST" });
export const deleteAccount = (token: string, id: string, reason: string) =>
  call<{ account: Account }>(`/accounts/${encodeURIComponent(id)}`, token, { method: "DELETE", body: JSON.stringify({ reason }) }).then((found) => {
    dropAccount(id);
    return found;
  });
export const restoreAccount = (token: string, id: string) =>
  call<{ account: Account }>(`/accounts/${encodeURIComponent(id)}/restore`, token, { method: "POST", body: "{}" });
export const purgeAccount = (token: string, id: string, confirm: string) =>
  call<{ purged: string }>(`/accounts/${encodeURIComponent(id)}`, token, { method: "DELETE", body: JSON.stringify({ permanent: true, confirm }) });
export const account = (token: string, id: string, allPickups = false) =>
  get<AccountDetail>(`/accounts/${encodeURIComponent(id)}${allPickups ? "?pickups=all" : ""}`, token);
export interface PickupEntry {
  id: string;
  at: string;
  raw_gal: number;
  scrap_gal: number;
  corrects_id?: string;
  load_id?: string;
  /** The stop of the pickup: the server refuses a second pickup for it (SO-1080). */
  route_stop_id?: string;
}
export const recordPickup = (token: string, accountId: string, entry: PickupEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/pickups`, token, {
    method: "POST",
    body: JSON.stringify(entry),
  }));
export interface CallEntry {
  id: string;
  caller: string;
  reason: Reason;
  at_fault: boolean;
  result: string;
  /** With the reason pickup_request, also record a pickup request (rule P-6). */
  make_request?: boolean;
}
export const logCall = (token: string, accountId: string, entry: CallEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/calls`, token, {
    method: "POST",
    body: JSON.stringify(entry),
  }));
/** A GeoJSON polygon: rings of [lng, lat] positions, the first ring is the outline. */
export interface GeoPolygon {
  type: "Polygon";
  coordinates: number[][][];
}
export interface Area {
  id: string;
  code: string;
  region: string;
  rotation_week: number;
  polygon: GeoPolygon | null;
  polygon_source: "auto" | "user";
  colour: string;
  can_edit_shape: boolean;
}
export interface PlanChange {
  /** Empty: the API gives the area whose shape holds the pin (rule V-20). */
  area_id?: string;
  week_group: string;
  window_start: string | null;
  window_end: string | null;
  service_minutes: number | null;
  stated_rate: number;
  season_start?: string | null;
  season_end?: string | null;
  blackouts?: Blackout[];
}
export interface AccountChange {
  status?: Account["status"];
  commercial_status?: Commercial;
  plan?: PlanChange;
}
export const areas = async (token: string) => (await get<{ areas: Area[] }>("/areas", token)).areas;
export const saveAreaPolygon = (token: string, id: string, polygon: GeoPolygon) =>
  call<Area>(`/areas/${encodeURIComponent(id)}/polygon`, token, { method: "PUT", body: JSON.stringify({ polygon }) });
export const resetAreaPolygon = (token: string, id: string) =>
  call<Area>(`/areas/${encodeURIComponent(id)}/polygon/reset`, token, { method: "POST" });
export type AreaVersionSource = "auto" | "user" | "revert";
/** Rule V-20: one kept shape of an area. The newest is `current`. */
export interface AreaVersion {
  id: string;
  polygon: GeoPolygon | null;
  vertices: number;
  source: AreaVersionSource;
  actor: string;
  at: string;
  note: string;
  current: boolean;
}
export const areaVersions = (token: string, id: string) =>
  get<{ versions: AreaVersion[]; more: boolean }>(`/areas/${encodeURIComponent(id)}/versions`, token);
export const revertAreaPolygon = (token: string, id: string, versionId: string) =>
  call<Area>(`/areas/${encodeURIComponent(id)}/polygon/revert`, token, {
    method: "POST",
    body: JSON.stringify({ version_id: versionId }),
  });
export type ShapeImportResult = "ok" | "colour" | "unchanged" | "no_area" | "bad_shape" | "not_allowed";
/** Rule V-20: one area code of an imported GeoJSON file, with its shape after the checks. */
export interface ShapeImportItem {
  file: string;
  code: string;
  result: ShapeImportResult;
  problem: string;
  warnings: string[];
  corners_in: number;
  corners: number;
  colour: string | null;
  area_id: string | null;
  polygon: GeoPolygon | null;
}
export interface ShapeImportReply {
  dry_run: boolean;
  items: ShapeImportItem[];
  counts: Record<ShapeImportResult, number>;
  /** Only on a dry run: the accounts with no plan that the new shapes give an area. */
  would_assign?: number;
  written: { shapes: number; colours: number } | null;
  assign: Record<string, number> | null;
}
export interface ShapeFile {
  name: string;
  geojson: unknown;
}
export const importAreaShapes = (token: string, files: ShapeFile[], dryRun: boolean, useColour: boolean) =>
  call<ShapeImportReply>("/areas/shapes/import", token, {
    method: "POST",
    body: JSON.stringify({ files, dry_run: dryRun, use_colour: useColour }),
  });
export interface Person {
  id: string;
  email: string;
}
export interface SalesArea {
  id: string;
  code: string;
  region: string;
  rotation_week: number;
  colour: string;
  has_polygon: boolean;
  salespeople: Person[];
  can_edit: boolean;
  pickup_source?: PickupSource;
  app_from?: string | null;
  /** Design 26.11 F4: an older API does not send it; then pickup_source is the state. */
  switch_state?: DataSourceState;
  can_switch?: boolean;
}
export type PickupSource = "sheet" | "app";
/** Design 26.11 F4: what a switch, a cancel or a switch back changes, before the confirm. */
export interface SwitchPreview {
  area: string;
  state: DataSourceState;
  to: PickupSource;
  accounts: number;
  rows: { account_id: string; number: string; name: string }[];
  import_run: { id: string; at: string } | null;
  choices: { tomorrow: string; rotation_week: string; cycle: string };
  app_from: string | null;
  sheet_pickups_from?: number;
  sheet_pickups_cycle?: number;
  frozen_fields?: string[];
  issues?: Record<string, number>;
  writes?: number;
  by_column?: Record<string, number>;
  write_rows?: { account_id: string; number: string; column: string; value: string | null }[];
  new_accounts?: number;
  sheet_pickups_count_again?: number;
}
export interface SalesAreas {
  areas: SalesArea[];
  owners: Person[];
  can_edit: boolean;
  /** Design 26.11 F8: an older API does not send it, and the page shows no add form. */
  can_create?: boolean;
}
export const salesAreas = (token: string) => get<SalesAreas>("/settings/sales-areas", token);
export const createArea = (token: string, code: string) => call<SalesAreas>("/areas", token, { method: "POST", body: JSON.stringify({ code }) });
export const setAreaSalespeople = (token: string, id: string, memberIds: string[]) =>
  call<SalesAreas>(`/areas/${encodeURIComponent(id)}/salespeople`, token, { method: "PUT", body: JSON.stringify({ member_ids: memberIds }) });
export const setAreaPickupSource = (token: string, id: string, source: PickupSource, appFrom?: string) =>
  call<SalesAreas>(`/areas/${encodeURIComponent(id)}/pickup-source`, token, {
    method: "PUT",
    body: JSON.stringify(appFrom ? { source, app_from: appFrom } : { source }),
  });
export const switchPreview = (token: string, id: string, to: PickupSource, appFrom?: string) =>
  get<SwitchPreview>(
    `/areas/${encodeURIComponent(id)}/switch-preview?to=${to}${appFrom ? `&app_from=${encodeURIComponent(appFrom)}` : ""}`,
    token,
  );
export const setAccountLocation = (token: string, id: string, change: LocationChange) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(id)}/location`, token, { method: "PUT", body: JSON.stringify(change) }));
export interface SensorChange {
  sensor_type: SensorType;
  float_pct?: number;
}
export const setContainerSensor = (token: string, accountId: string, containerId: string, change: SensorChange) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/containers/${encodeURIComponent(containerId)}/sensor`, token, {
    method: "PUT",
    body: JSON.stringify(change),
  }));
/** One change of a point; `start` is the point before the first change, with no time. */
export interface PinEntry {
  id: string;
  at: string | null;
  by: string | null;
  source: PointSource | null;
  lat: number | null;
  lng: number | null;
  accuracy_m: number | null;
  cleared: boolean;
}
export const pinHistory = (token: string, id: string, point: "door" | "tank" = "tank") =>
  get<{ history: PinEntry[]; can_restore: boolean }>(`/accounts/${encodeURIComponent(id)}/location/history?point=${point}`, token);
export const mapsConfig = (token: string) => get<MapsConfig>("/maps/config", token);
export type PinKind = "accounts" | "leads";
export type PinLayer = "door" | "tank";
/** One pin of the map; `fallback` marks a tank layer pin at the front door (rule G-2). */
export interface Pin extends LatLng {
  id: string;
  /** The account number; null on a lead, absent from an older API. */
  number?: string | null;
  name: string;
  status: string;
  address: string;
  source: PointSource;
  fallback: boolean;
  check?: boolean;
  /** The median estimated fill share of today; null: no estimate. Absent on a lead and from an older API. */
  fill?: number | null;
  /** A fresh float report that holds the fill. Absent from an older API. */
  float?: FloatSide | null;
  /** Absent on a lead and from an older API. */
  data_source?: DataSource;
  /** The area code of the plan; null: no plan. Absent on a lead and from an older API. */
  area?: string | null;
  /** Absent when the pin is in the area of the plan, on a lead and from an older API. */
  outside_area?: OutsideArea;
}
export interface PinList {
  pins: Pin[];
  total: number;
  truncated: boolean;
  unplaced: number;
  limit: number;
}
export const mapPins = (token: string, kind: PinKind, layer: PinLayer, filters: Record<string, string>, bbox: string | null, signal?: AbortSignal) =>
  get<PinList>(`/map/pins?${new URLSearchParams({ ...filters, kind, layer, ...(bbox ? { bbox } : {}) })}`, token, signal);
export const changeAccount = (token: string, id: string, change: AccountChange) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify(change) }));
/** A start day of null clears the reset (rule A-5). */
export interface ForecastReset {
  start: string | null;
  reason: string;
  week_group?: string;
}
export const resetForecast = (token: string, id: string, reset: ForecastReset) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(id)}/forecast-reset`, token, { method: "POST", body: JSON.stringify(reset) }));
export interface ForecastLimits {
  limits: Limits;
  defaults: Limits;
  can_edit: boolean;
}
export const forecastLimits = (token: string) => get<ForecastLimits>("/settings/forecast", token);
export const setForecastLimits = (token: string, limits: Limits) =>
  call<ForecastLimits>("/settings/forecast", token, { method: "PUT", body: JSON.stringify(limits) });
export interface SensorRuleSettings {
  limits: SensorRules;
  defaults: SensorRules;
  can_edit: boolean;
}
export const sensorRules = (token: string) => get<SensorRuleSettings>("/settings/sensor-rules", token);
export const setSensorRules = (token: string, rules: SensorRules) =>
  call<SensorRuleSettings>("/settings/sensor-rules", token, { method: "PUT", body: JSON.stringify(rules) });
export interface Organization {
  slug: string;
  name: string;
  current: boolean;
}
export const organizations = async (token: string) =>
  (await get<{ organizations: Organization[] }>("/me/organizations", token)).organizations;
export type Notifications = { follow_up_mail: boolean; issue_reminder_mail: boolean };
export const notifications = (token: string) => get<Notifications>("/me/notifications", token);
export const setNotifications = (token: string, next: Notifications) =>
  call<Notifications>("/me/notifications", token, { method: "PUT", body: JSON.stringify(next) });
export type MenuPrefs = { open_all: boolean };
export const menuPrefs = (token: string) => get<MenuPrefs>("/me/menu", token);
export const setMenuPrefs = (token: string, next: MenuPrefs) =>
  call<MenuPrefs>("/me/menu", token, { method: "PUT", body: JSON.stringify(next) });
export type IssueReminder = { days: number; default: number; max: number; can_edit: boolean };
export const issueReminder = (token: string) => get<IssueReminder>("/settings/issue-reminder", token);
export type TelegramAlerts = {
  enabled: boolean;
  chat_id: string;
  rules: string[];
  since: string | null;
  choices: { rule: string; label: string }[];
  chats: string[];
  can_edit: boolean;
};
export const telegramAlerts = (token: string) => get<TelegramAlerts>("/settings/telegram-alerts", token);
export const setTelegramAlerts = (token: string, body: { enabled: boolean; chat_id: string; rules: string[] }) =>
  call<TelegramAlerts>("/settings/telegram-alerts", token, { method: "PUT", body: JSON.stringify(body) });
export type PortalContactSetting = { phone: string; email: string; phone_max: number; email_max: number; can_edit: boolean };
export const portalContact = (token: string) => get<PortalContactSetting>("/settings/portal-contact", token);
export const setPortalContact = (token: string, body: { phone: string; email: string }) =>
  call<PortalContactSetting>("/settings/portal-contact", token, { method: "PUT", body: JSON.stringify(body) });
export type PlaceCheckSetting = { far_m: number; enabled?: boolean; default: number; min: number; max: number; can_edit: boolean };
export const placeCheck = (token: string) => get<PlaceCheckSetting>("/settings/place-check", token);
export const setPlaceCheck = (token: string, body: { far_m: number; enabled?: boolean }) =>
  call<PlaceCheckSetting>("/settings/place-check", token, { method: "PUT", body: JSON.stringify(body) });
export type MessageMailMode = "salesperson" | "managers" | "off";
export type MessageMail = { mode: MessageMailMode; default: MessageMailMode; choices: { mode: MessageMailMode; label: string }[]; can_edit: boolean };
export const messageMail = (token: string) => get<MessageMail>("/settings/message-mail", token);
export const setMessageMail = (token: string, mode: MessageMailMode) =>
  call<MessageMail>("/settings/message-mail", token, { method: "PUT", body: JSON.stringify({ mode }) });
export const setIssueReminder = (token: string, days: number) =>
  call<IssueReminder>("/settings/issue-reminder", token, { method: "PUT", body: JSON.stringify({ days }) });
export const pins = async (token: string) => (await get<{ pins: string[] }>("/me/pins", token)).pins;
export const setPins = async (token: string, pages: string[]) =>
  (await call<{ pins: string[] }>("/me/pins", token, { method: "PUT", body: JSON.stringify({ pins: pages }) })).pins;
export interface IssueEntry {
  id: string;
  reason: IssueReason;
  source: "screen" | "call";
  note: string;
  assignee_id: string;
  due_date: string | null;
}
export interface IssueStep {
  id: string;
  kind: StepKind;
  assignee_id?: string;
  resolution?: Resolution;
  note: string;
}
export interface Assignee {
  id: string;
  email: string;
  roles?: string[];
}
export const openIssue = (token: string, accountId: string, entry: IssueEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/issues`, token, {
    method: "POST",
    body: JSON.stringify(entry),
  }));
export const stepIssue = (token: string, issueId: string, step: IssueStep) =>
  call<{ issue: Issue | null; events: IssueEvent[] }>(`/issues/${encodeURIComponent(issueId)}/events`, token, {
    method: "POST",
    body: JSON.stringify(step),
  });
export const assignees = async (token: string, accountId: string) =>
  (await get<{ assignees: Assignee[] }>(`/accounts/${encodeURIComponent(accountId)}/assignees`, token)).assignees;
export const issues = async (token: string, mine: boolean, status: "open" | "closed") =>
  (await get<{ issues: Issue[] }>(`/issues?mine=${mine}&status=${status}`, token)).issues;
export interface OrderEntry {
  id: string;
  kind: OrderKind;
  from_size: number | null;
  to_size: number | null;
  note: string;
}
export interface OrderStep {
  id: string;
  kind: "done" | "cancel";
  note: string;
  drop_serial?: string | null;
  pull_serial?: string | null;
}
export interface PickCount {
  size_gal: number;
  count: number;
}
export const openWorkOrder = (token: string, accountId: string, entry: OrderEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/work-orders`, token, {
    method: "POST",
    body: JSON.stringify(entry),
  }));
export const stepWorkOrder = (token: string, orderId: string, step: OrderStep) =>
  call<{ order: WorkOrder | null }>(`/work-orders/${encodeURIComponent(orderId)}/events`, token, {
    method: "POST",
    body: JSON.stringify(step),
  });
export const workOrders = (token: string, status: "open" | "closed") =>
  get<{ orders: WorkOrder[]; pick_list: PickCount[] }>(`/work-orders?status=${status}`, token);
export interface Containers {
  sizes: number[];
  margin: number;
}
export type UnitStatus = "available" | "staged" | "loaded" | "deployed" | "reclaimed" | "reconditioning" | "retired";
export type UnitPlace = "hub" | "truck" | "account";
export interface Unit {
  id: string;
  serial: string;
  size_gal: number;
  status: UnitStatus;
  place: UnitPlace;
  hub_id: string | null;
  hub_name: string | null;
  truck: string | null;
  account_id: string | null;
  account_number: string | null;
  account_name: string | null;
  note: string;
  created_at: string;
  updated_at: string;
  last_move_at?: string | null;
}
export interface UnitMove {
  at: string;
  from_status: UnitStatus | null;
  from_where: string;
  to_status: UnitStatus;
  to_where: string;
  work_order_id: string | null;
  note: string;
  moved_by: string | null;
}
export interface UnitList {
  units: Unit[];
  hubs: Hub[];
  sizes: number[];
  can_add: boolean;
  can_move: boolean;
}
export interface UnitDetail {
  unit: Unit;
  moves: UnitMove[];
  next: UnitStatus[];
  hubs: Hub[];
  can_move: boolean;
}
export interface UnitEntry {
  serial?: string;
  prefix?: string;
  first?: string;
  last?: string;
  size_gal: number;
  hub_id: string | null;
  note: string;
}
export interface UnitMoveEntry {
  status: UnitStatus;
  place: UnitPlace;
  hub_id?: string | null;
  truck?: string | null;
  account_number?: string | null;
  note: string;
}
export interface DeployedUnit {
  id: string;
  serial: string;
  size_gal: number;
}
export const units = (token: string) => get<UnitList>("/units", token);
export const unit = (token: string, unitId: string) => get<UnitDetail>(`/units/${encodeURIComponent(unitId)}`, token);
export const addUnits = (token: string, entry: UnitEntry) =>
  call<{ added: number; ids: string[] }>("/units", token, { method: "POST", body: JSON.stringify(entry) });
export const moveUnit = (token: string, unitId: string, entry: UnitMoveEntry) =>
  call<UnitDetail>(`/units/${encodeURIComponent(unitId)}/move`, token, { method: "POST", body: JSON.stringify(entry) });
export interface ContainerSettings extends Containers {
  defaults: Containers;
  can_edit: boolean;
}
export const containerSizes = (token: string) => get<ContainerSettings>("/settings/containers", token);
export const setContainerSizes = (token: string, containers: Containers) =>
  call<ContainerSettings>("/settings/containers", token, { method: "PUT", body: JSON.stringify(containers) });
export interface ReviewItem {
  account_id: string;
  number: string;
  name: string;
  status: Account["status"];
  week_group: string;
  stated_rate: number;
  capacity_gal: number;
  can_decide: boolean;
  sizes: number[];
  kind: ReviewKind;
  reason: string;
  short: string;
  gap: number | null;
  drop_preset: number | null;
  cycle_weeks: number | null;
  averages: number[];
  measured_rate: number;
  cap_weeks: number | null;
  weeks_out: number | null;
  scrap: number;
  need_gal: number | null;
  recommended_sizes: number[] | null;
  recommended_gal: number | null;
}
export interface DuplicatePickup {
  id: string;
  at: string;
  raw_gal: number;
  scrap_gal: number;
  source: "seed" | "screen" | "import";
  visit_id: string | null;
}
export interface DuplicateItem {
  account_id: string;
  number: string;
  name: string;
  week: string;
  week_end: string;
  pickups: DuplicatePickup[];
  can_ack: boolean;
}
/** SO-956: a phone visit older than the offline window, for the office to check. */
export interface OldEntry {
  visit_id: string;
  account_id: string;
  number: string;
  name: string;
  at: string;
  made_at: string;
  kind: VisitKind;
  raw_gal: number | null;
  scrap_gal: number | null;
  gallons_left: number | null;
  reason: VisitReason | null;
  driver_name: string | null;
  confirmed: boolean;
  window_hours: number;
  can_check: boolean;
}
export interface Review {
  cycle_start: string;
  cycle_end: string;
  container_sizes: number[];
  kinds: { kind: ReviewKind; items: ReviewItem[] }[];
  duplicates?: DuplicateItem[];
  old_entries?: OldEntry[];
  /** Rule D-19: the phone entries of the last 7 days that the driver confirmed away from the stop. An older API does not send it. */
  place_entries?: PlaceEntry[];
}
/** The place check of a phone entry (rule D-19): numbers only, never the position. */
export interface Place {
  check: "near" | "far" | "denied" | "no_fix";
  distance_m?: number | null;
  accuracy_m?: number | null;
}
export interface PlaceEntry {
  visit_id: string;
  account_id: string;
  number: string;
  name: string;
  at: string;
  kind: VisitKind;
  raw_gal: number | null;
  scrap_gal: number | null;
  gallons_left: number | null;
  reason: VisitReason | null;
  driver_name: string | null;
  place: Place;
}
export const checkOldEntries = (token: string, visitIds: string[]) =>
  call<{ checked: number; unchanged: number }>("/review/old-entries/check", token, {
    method: "POST",
    body: JSON.stringify({ visit_ids: visitIds }),
  });
export interface DuplicateWeek {
  account_id: string;
  week: string;
  pickup_ids: string[];
}
export const ackDuplicates = (token: string, items: DuplicateWeek[], note = "") =>
  call<{ acked: number; unchanged: number }>("/review/duplicates/ack", token, {
    method: "POST",
    body: JSON.stringify({ items, note }),
  });
export interface ReviewDecision {
  id: string;
  kind: ReviewKind;
  result: ReviewResult;
  note: string;
  stated_rate?: number;
  week_group?: string;
  from_size?: number;
  to_size?: number;
  assignee_id?: string;
}
export const review = (token: string) => get<Review>("/review", token);
export const decideReview = (token: string, accountId: string, decision: ReviewDecision) =>
  call<Review>(`/accounts/${encodeURIComponent(accountId)}/review-decisions`, token, {
    method: "POST",
    body: JSON.stringify(decision),
  });
export interface LoadEntry {
  id: string;
  day: string;
  truck: string;
  driver_id?: string;
}
export interface LoadDrivers {
  /** driver: the member has the driver role; an older API does not send it. */
  drivers?: { id: string; email: string; name?: string; driver?: boolean }[];
  truck_drivers?: Record<string, string>;
}
export interface ResultEntry {
  id: string;
  raw_gal: number;
  scrap_pct: number;
  corrects_id: string | null;
}
const loadPath = (id: string) => `/loads/${encodeURIComponent(id)}`;
export const loads = (token: string, day?: string) =>
  get<{ loads: Load[]; can_make: boolean } & LoadDrivers>(`/loads${day ? `?day=${day}` : ""}`, token);
export const makeLoad = (token: string, entry: LoadEntry) =>
  call<{ load: Load | null }>("/loads", token, { method: "POST", body: JSON.stringify(entry) });
export const recordLoadResult = (token: string, loadId: string, entry: ResultEntry) =>
  call<{ load: Load | null }>(`${loadPath(loadId)}/results`, token, { method: "POST", body: JSON.stringify(entry) });
export const approveLoad = (token: string, loadId: string, id: string) =>
  call<{ load: Load | null }>(`${loadPath(loadId)}/approval`, token, { method: "POST", body: JSON.stringify({ id }) });
export interface TruckFit {
  size_gal: number;
  fits: boolean;
}
export interface CycleCapacity {
  stops: number;
  gallons: number;
  gal_per_stop: number;
  hours: number;
  truck_days: number;
  gal_per_truck_day: number;
  trucks: TruckFit[];
}
export interface CycleStop {
  account_id: string;
  number: string;
  name: string;
  address: string;
  data_source?: DataSource;
  status: Account["status"];
  area: string;
  area_id: string;
  week_group: string;
  window: [string | null, string | null];
  service_minutes: number;
  capacity_gal: number;
  stated_rate: number;
  weeks_out: number | null;
  weeks_left: number | null;
  estimated_gal: number;
  from_pickups: boolean;
  from_estimate?: boolean;
  load_share: number | null;
  flags: Flag[];
  selected: boolean;
  default: boolean;
  added: boolean;
  picked_up: string | null;
  chosen_at: string | null;
  can_choose: boolean;
  blackouts?: Blackout[];
  season?: [string, string] | null;
  off_season?: boolean;
  early_pull?: boolean;
  next_visit?: string | null;
  projected_share?: number | null;
  overflow_risk?: number | null;
}
export interface Holiday {
  id: string;
  day: string;
  name: string;
}
export interface CycleBoard {
  week: number;
  week_start: string;
  week_end: string;
  visit_day?: string;
  holidays?: Holiday[];
  early_margin?: number;
  early_risk?: number;
  this_week: number;
  truck_sizes: number[];
  stops: CycleStop[];
  areas: { area: string; listed: number; capacity: CycleCapacity }[];
  capacity: CycleCapacity;
  can_choose: boolean;
}
export interface CycleChoice {
  id: string;
  account_id: string;
  week_start: string;
  selected: boolean;
}
export const cycleBoard = (token: string, week?: number) => get<CycleBoard>(`/cycle${week ? `?week=${week}` : ""}`, token);
export const chooseStop = (token: string, choice: CycleChoice) =>
  call<CycleBoard>("/cycle/selections", token, { method: "POST", body: JSON.stringify(choice) });
export async function cycleCsv(token: string, week: number): Promise<string> {
  const response = await fetch(`/api/cycle/stops.csv?week=${week}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new ApiError(response.status);
  return response.text();
}
export interface RouteStop {
  id: string;
  account_id: string;
  number: string;
  name: string;
  address: string;
  data_source?: DataSource;
  area: string | null;
  window: [string | null, string | null];
  position: number;
  state: "planned" | "done" | "skipped" | "measured" | "partial" | "no_access";
  reason: string;
  /** Why a partly emptied tank is not empty (rule D-16). */
  outcome_reason?: "frozen" | "truck_full" | null;
  /** The gallons left in a measured or partly emptied tank, from the ruler. */
  gallons_left?: number | null;
  /** The gallons of the pickup of a done or partly emptied stop, from its current visit. */
  raw_gal?: number | null;
  scrap_gal?: number | null;
  /** The saved pickup of a planned stop that no visit names (SO-1080). An older API sends none. */
  saved_pickup?: { id: string; raw_gal: number; scrap_gal: number } | null;
  /** The arrival time of the accepted plan; a stop that was added by hand has none. */
  arrive?: string | null;
  planned_gal?: number | null;
  estimated_gal: number | null;
  /** The size of all containers of the account. An older API does not send it. */
  capacity_gal?: number | null;
  phone: string;
  access_note: string;
  blackouts?: { start: string; end: string }[];
  photos?: number;
  door?: LatLng | null;
  tank?: StopTank | null;
  can_pin?: boolean;
  /** Rule V-10: an area-mode route, and the area of the account is the home of another truck. */
  off_route?: boolean;
  /** Rule P-6: the account has a pickup request that no pickup collected before this day. */
  requested?: boolean;
  /** Rule O-3: the minutes after the planned arrival of a late open stop of today. An older API does not send it. */
  late_min?: number | null;
}
export interface Route {
  id: string;
  truck: string;
  size_gal: number | null;
  driver_id: string;
  driver_email: string | null;
  status: "planned" | "started" | "done" | "cancelled";
  mine: boolean;
  started_at?: string | null;
  odometer_start?: number | null;
  odometer_end?: number | null;
  check?: RouteCheck | null;
  can_check?: boolean;
  last_odometer?: number | null;
  stops: RouteStop[];
  totals: { stops: number; gallons: number; hours: number; stops_per_hour?: number; fits: boolean | null };
}
export interface RouteCheck {
  at: string;
  odometer: number;
  failed: number;
  results: { item_id: string; label: string; ok: boolean; note: string }[];
}
export interface RouteStart {
  odometer: number;
  results: { item_id: string; ok: boolean; note: string }[];
}
export interface RouteDay {
  day: string;
  today: string;
  /** Rule D-19: the distance from the stop pin that asks for a confirm. An older API does not send it. */
  place_far_m?: number;
  /** An older API does not send it: the check is on. */
  place_check_on?: boolean;
  first_day: string;
  last_day: string;
  week: number | null;
  week_start: string | null;
  can_plan: boolean;
  can_see_plan?: boolean;
  truck_sizes: number[];
  trucks?: { code: string; capacity_gal: number; depot: string }[];
  drivers: { id: string; email: string; name?: string; driver?: boolean; preferred_truck?: string | null }[];
  routes: Route[];
  unrouted: CycleStop[];
  check_items?: { id: string; label: string }[];
}
export interface RouteEntry {
  id: string;
  day: string;
  truck: string;
  size_gal: number | null;
  driver_id: string;
}
export interface RouteChange {
  truck?: string;
  size_gal?: number | null;
  driver_id?: string;
  cancel?: boolean;
}
export const routeDay = (token: string, day?: string) => get<RouteDay>(`/routes${day ? `?day=${day}` : ""}`, token);
export const makeRoute = (token: string, entry: RouteEntry) =>
  call<RouteDay>("/routes", token, { method: "POST", body: JSON.stringify(entry) });
export const changeRoute = (token: string, id: string, change: RouteChange) =>
  call<RouteDay>(`/routes/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify(change) });
export const addRouteStop = (token: string, routeId: string, stop: { id: string; account_id?: string; number?: string }) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/stops`, token, { method: "POST", body: JSON.stringify(stop) });
export const moveRouteStop = (token: string, routeId: string, stopId: string, step: -1 | 1) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/stops/${encodeURIComponent(stopId)}/move`, token, {
    method: "POST",
    body: JSON.stringify({ step }),
  });
export const removeRouteStop = (token: string, routeId: string, stopId: string) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/stops/${encodeURIComponent(stopId)}`, token, { method: "DELETE" });
export const visitRouteStop = (
  token: string,
  routeId: string,
  stopId: string,
  visit: { state: "done" | "skipped" | "measured" | "partial" | "no_access"; reason?: string; gallons_left?: number; raw_gal?: number; scrap_gal?: number; load_id?: string },
) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/stops/${encodeURIComponent(stopId)}/visit`, token, {
    method: "POST",
    body: JSON.stringify(visit),
  });
export const startRoute = (token: string, routeId: string, start: RouteStart) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/start`, token, { method: "POST", body: JSON.stringify(start) });
export const finishRoute = (token: string, routeId: string, odometer: number) =>
  call<RouteDay>(`/routes/${encodeURIComponent(routeId)}/finish`, token, { method: "POST", body: JSON.stringify({ odometer }) });
export interface PreTripList {
  items: { id: string; label: string }[];
  can_edit: boolean;
}
export const preTrip = (token: string) => get<PreTripList>("/settings/pre-trip", token);
export const setPreTrip = (token: string, items: { id: string | null; label: string }[]) =>
  call<PreTripList>("/settings/pre-trip", token, { method: "PUT", body: JSON.stringify({ items }) });
export const fillRoutes = (token: string, day: string) =>
  call<RouteDay>("/routes/fill", token, { method: "POST", body: JSON.stringify({ day }) });
export type PlanStatus = "running" | "ready" | "failed" | "accepted";
export type PlanWhy = "request" | "risk" | "follow_up" | "min_visit" | "full" | "near";
export interface PlanStop {
  truck: string;
  seq: number;
  kind: "stop" | "dump";
  account_id: string | null;
  number: string | null;
  name: string | null;
  address: string | null;
  area: string | null;
  lat: number | null;
  lng: number | null;
  arrive: string;
  expected_gal: number | null;
  fill_p10: number | null;
  fill_p50: number | null;
  fill_p90: number | null;
  risk: number | null;
  why: PlanWhy | null;
  off_route?: boolean;
  /** The service window of the account, "HH:MM". Absent from an older API. */
  window_start?: string | null;
  window_end?: string | null;
  /** The fill of the account now (rule S-8). Absent from an older API. */
  fill_now?: Pick<FillBand, "p10" | "p50" | "p90"> | null;
  /** The day of a pickup that the plan did not know (rule S-8). Absent from an older API. */
  emptied_on?: string | null;
  /** A fresh float report (SO-2500): the side, the height, the top load below the float and the risk of today. Absent from an older API. */
  float_now?: (FloatSide & { max_gal: number | null; risk: number }) | null;
  /** The planned minutes at the stop or the dump. Absent from an older API or an older plan. */
  service_min?: number | null;
}
export interface PlanTruck {
  truck: string;
  depot_id: string | null;
  capacity: number | null;
  start: number | null;
  end: number | null;
  drive_min: number | null;
  /** The planned minutes at all stops of the truck. Absent from an older API. */
  service_min?: number | null;
  gallons: number | null;
  dumps: number | null;
  /** The day that a new stop order of its areas starts (rule V-13). */
  template_until?: string | null;
  accepted: boolean;
  route_id: string | null;
  default_driver_id: string | null;
  /** The road path from the planner, an encoded polyline at precision 6. None: straight lines. */
  shape?: string | null;
  stops: PlanStop[];
}
export interface PlanSummary {
  day?: string;
  accounts?: number;
  candidates?: number;
  trucks?: number;
  trucks_used?: number;
  stops?: number;
  gallons?: number;
  drive_h?: number;
  mid_day_dumps?: number;
  tanks_at_risk_left?: number;
  min_visit_days?: number;
  min_visits_left?: number;
  requests_left?: number;
  mode?: PlannerMode;
  mode_used?: PlannerMode;
  truck_preference?: TruckPreference;
  home_trucks_out?: number;
  off_route_stops?: number;
  template_days?: number;
  templates_kept?: number;
  templates_made?: number;
  template_new_accounts?: number;
  counts?: Record<string, number>;
}
export interface RoutePlan {
  id: string;
  status: PlanStatus;
  made_at: string;
  finished_at: string | null;
  made_by: string | null;
  nightly: boolean;
  error: string;
  summary: PlanSummary;
  trucks: PlanTruck[];
}
export interface PlanDepot {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
}
export interface PlanView {
  day: string;
  today: string;
  workday: boolean;
  can_plan: boolean;
  plan: RoutePlan | null;
  /** The overflow risk at which a stop is an early pull (D-15). */
  early_risk?: number;
  depots: PlanDepot[];
}
export const routePlan = (token: string, day: string) => get<PlanView>(`/routes/plan?day=${day}`, token);
export interface FillStop {
  account_id: string;
  number: string | null;
  /** Absent from an older API. */
  name?: string | null;
  lat: number | null;
  lng: number | null;
  /** The median estimated fill share on the day; 1 is full. */
  fill: number | null;
  band: number | null;
  /** A fresh float report on the day of today (SO-2500). Absent from an older API. */
  float?: FloatSide | null;
}
export interface FillRoute {
  truck: string;
  colour: number;
  depot_id: string | null;
  /** True for a truck of the newest proposed plan that has no saved route on the day. */
  proposed: boolean;
  /** The road path, an encoded polyline at precision 6. None: straight lines. */
  shape?: string | null;
  stops: FillStop[];
}
export interface FillTank {
  account_id: string;
  number: string;
  lat: number | null;
  lng: number | null;
  fill: number;
  risk: number;
  band: number;
  truck: string | null;
  /** Absent from an older API. */
  name?: string;
  /** The first saved or proposed stop on or after the shown day. Absent from an older API. */
  next_visit?: string | null;
  /** A fresh float report on the day of today (SO-2500). Absent from an older API. */
  float?: FloatSide | null;
}
export interface FillDay {
  day: string;
  today: string;
  last_day: string;
  /** "mixed": the saved routes and the plan trucks with no saved route. */
  source: "routes" | "plan" | "mixed" | "none";
  routes: FillRoute[];
  tanks: FillTank[];
  bands: number[];
  high_share: number;
  depots: PlanDepot[];
}
export const fillDay = (token: string, day?: string, signal?: AbortSignal) => get<FillDay>(`/fill-view${day ? `?day=${day}` : ""}`, token, signal);
export const planRoutes = (token: string, id: string, day: string) =>
  call<PlanView>("/routes/plan", token, { method: "POST", body: JSON.stringify({ id, day }) });
export const acceptPlan = (token: string, planId: string, trucks: { truck: string; driver_id: string }[]) =>
  call<{ plan: PlanView; routes: RouteDay }>(`/routes/plan/${encodeURIComponent(planId)}/accept`, token, {
    method: "POST",
    body: JSON.stringify({ trucks }),
  });
export interface CalendarValue {
  week_starts: number;
  cycle_start: string;
  holiday_rule: "next" | "previous";
  early_margin: number;
  early_risk: number;
}
export interface CalendarSettings {
  calendar: CalendarValue;
  defaults: CalendarValue;
  can_edit: boolean;
}
export const calendarSettings = (token: string) => get<CalendarSettings>("/settings/calendar", token);
export const setCalendarSettings = (token: string, calendar: CalendarValue) =>
  call<CalendarSettings>("/settings/calendar", token, { method: "PUT", body: JSON.stringify(calendar) });
export interface HolidayList {
  holidays: Holiday[];
  can_edit: boolean;
}
export type PlannerMode = "auto" | "areas";
export type TruckPreference = "hard" | "soft";
export interface PlannerValue {
  min_visit_days: number;
  inspect_weight: number;
  mode: PlannerMode;
  truck_preference: TruckPreference;
  template_days: number;
}
export interface PlannerSettings {
  planner: PlannerValue;
  defaults: PlannerValue;
  can_edit: boolean;
}
export const plannerSettings = (token: string) => get<PlannerSettings>("/settings/planner", token);
export const setPlannerSettings = (token: string, planner: PlannerValue) =>
  call<PlannerSettings>("/settings/planner", token, { method: "PUT", body: JSON.stringify(planner) });
export const holidays = (token: string) => get<HolidayList>("/settings/holidays", token);
export const addHoliday = (token: string, holiday: Holiday) =>
  call<HolidayList>("/settings/holidays", token, { method: "POST", body: JSON.stringify(holiday) });
export const removeHoliday = (token: string, id: string) =>
  call<HolidayList>(`/settings/holidays/${encodeURIComponent(id)}`, token, { method: "DELETE" });
export interface DepotFields {
  name: string;
  address: string;
  opens: string;
  closes: string;
}
export interface Depot extends DepotFields {
  id: string;
  lat: number | null;
  lng: number | null;
  /** From the geocode of the address or the pin editor (rule V-2). */
  point: MapPoint | null;
}
export interface TruckFields {
  code: string;
  hub_id: string;
  capacity_gal: number;
  shift_start: string;
  shift_end: string;
  active: boolean;
}
export interface AreaCode {
  id: string;
  code: string;
}
export interface Truck extends TruckFields {
  id: string;
  hub_name: string;
  routes: number;
  home_areas: AreaCode[];
}
export interface Fleet {
  depots: Depot[];
  trucks: Truck[];
  defaults: { opens: string; closes: string; shift_start: string; shift_end: string };
  areas: AreaCode[];
  can_edit: boolean;
  /** After a depot save: demo data was not sent to Google, so the point did not change (G-6). */
  address_not_sent?: boolean;
}
export const fleet = (token: string) => get<Fleet>("/settings/fleet", token);
export const setDepotLocation = (token: string, id: string, change: LocationChange) =>
  call<Fleet>(`/settings/fleet/depots/${encodeURIComponent(id)}/location`, token, { method: "PUT", body: JSON.stringify(change) });
export const addDepot = (token: string, depot: DepotFields & { id: string }) =>
  call<Fleet>("/settings/fleet/depots", token, { method: "POST", body: JSON.stringify(depot) });
export const changeDepot = (token: string, id: string, depot: DepotFields) =>
  call<Fleet>(`/settings/fleet/depots/${encodeURIComponent(id)}`, token, { method: "PUT", body: JSON.stringify(depot) });
export const addTruck = (token: string, truck: TruckFields & { id: string }) =>
  call<Fleet>("/settings/fleet/trucks", token, { method: "POST", body: JSON.stringify(truck) });
export const changeTruck = (token: string, id: string, truck: TruckFields) =>
  call<Fleet>(`/settings/fleet/trucks/${encodeURIComponent(id)}`, token, { method: "PUT", body: JSON.stringify(truck) });
export const removeTruck = (token: string, id: string) =>
  call<Fleet>(`/settings/fleet/trucks/${encodeURIComponent(id)}`, token, { method: "DELETE" });
export const setHomeAreas = (token: string, id: string, areaIds: string[]) =>
  call<Fleet>(`/settings/fleet/trucks/${encodeURIComponent(id)}/home-areas`, token, { method: "PUT", body: JSON.stringify({ area_ids: areaIds }) });
export interface ScorecardWeek {
  week_start: string;
  week_end: string;
  rotation_week: number;
  partial: boolean;
  calls_at_fault: number;
  overflows: number;
  overflows_per_100: number | null;
  planned_stops: number;
  done_stops: number;
  extra_stops: number;
  actual_gal: number;
  estimated_gal: number;
  accuracy: number | null;
  /** 0 to 100: 100 less the stop errors as a share of the estimate (rule K-2). An older API sends none. */
  accuracy_pct?: number | null;
  /** The actual gallons ÷ the estimated gallons − 1. */
  bias?: number | null;
}
export interface SalesWeek {
  week_start: string;
  wins: number;
  renewals: number;
  losses: number;
}
export interface SalesRow {
  key: string;
  name: string;
  wins: number;
  renewals: number;
  losses: number;
  minutes: number;
  hours_per_win: number | null;
  accounts: number;
  active: number | null;
  regular: number | null;
  on_call: number | null;
  signed_regular: number | null;
}
export interface Sales {
  weeks: SalesWeek[];
  people: SalesRow[];
  states: SalesRow[];
  lost_reasons: Record<string, number>;
  collectors: Record<string, number>;
}
export interface Scorecard {
  accounts: number;
  weeks: ScorecardWeek[];
  /** Null for a role that does not see leads. */
  sales: Sales | null;
}
export const scorecard = (token: string, weeks: number) => get<Scorecard>(`/scorecard?weeks=${weeks}`, token);

export interface Lead {
  id: string;
  name: string;
  address: string;
  phone: string;
  source: Source;
  stage: Stage;
  lost_reason: LostReason | null;
  lost_note: string;
  owner_id: string | null;
  owner_email: string | null;
  mine: boolean;
  qualifier: string;
  previous_collector: string;
  competitor_onsite: boolean;
  verified_at: string | null;
  verified_email: string | null;
  account_id: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  deleted?: Deleted | null;
  door?: MapPoint | null;
}
export interface LeadNote {
  id: string;
  kind: NoteKind;
  minutes: number | null;
  note: string;
  at: string;
  by_email: string | null;
}
export interface LeadMatch {
  kind: "lead" | "account";
  id: string;
  name: string;
  address: string;
  number: string | null;
}
export interface LeadDetail extends DeleteRights {
  lead: Lead | null;
  notes: LeadNote[];
  matches: LeadMatch[];
  can_assign: boolean;
  follow_ups: FollowUp[];
  deal_id?: string | null;
  can_deal?: boolean;
}
export interface LeadList {
  leads: Lead[];
  counts: Record<Stage, number>;
  more: boolean;
  can_add: boolean;
  can_own: boolean;
  can_assign: boolean;
  can_delete?: boolean;
}
export interface LeadEntry {
  id: string;
  name: string;
  address: string;
  phone: string;
  source: Source;
  owner_id: string | null;
  previous_collector: string;
  competitor_onsite: boolean;
  note: string;
}
export interface LeadChange {
  name?: string;
  address?: string;
  phone?: string;
  qualifier?: string;
  previous_collector?: string;
  competitor_onsite?: boolean;
  owner_id?: string;
  clear_owner?: boolean;
  stage?: Stage;
  lost_reason?: LostReason;
  lost_note?: string;
  verify?: boolean;
}
export interface LeadNoteEntry {
  id: string;
  kind: NoteKind;
  minutes: number | null;
  note: string;
}
export const leads = (token: string, view: LeadView, stage: Stage | null, deleted = false) =>
  get<LeadList>(`/leads?${new URLSearchParams({ view, ...(stage ? { stage } : {}), ...(deleted ? { deleted: "true" } : {}) })}`, token);
export const deleteLead = (token: string, id: string, reason: string) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(id)}`, token, { method: "DELETE", body: JSON.stringify({ reason }) });
export const restoreLead = (token: string, id: string) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(id)}/restore`, token, { method: "POST", body: "{}" });
export const purgeLead = (token: string, id: string, confirm: string) =>
  call<{ purged: string }>(`/leads/${encodeURIComponent(id)}`, token, { method: "DELETE", body: JSON.stringify({ permanent: true, confirm }) });
export const lead = (token: string, id: string) => get<LeadDetail>(`/leads/${encodeURIComponent(id)}`, token);
export const leadOwners = async (token: string) => (await get<{ owners: Assignee[] }>("/leads/owners", token)).owners;
/** The possible matches of a new lead before the save; an older API answers 404 or 405. */
export const leadMatches = async (token: string, place: { name: string; address: string; phone: string }) =>
  (await call<{ matches: LeadMatch[] }>("/leads/matches", token, { method: "POST", body: JSON.stringify(place) })).matches;
export const makeLead = (token: string, entry: LeadEntry) =>
  call<LeadDetail>("/leads", token, { method: "POST", body: JSON.stringify(entry) });
export const setLeadLocation = (token: string, id: string, change: LocationChange) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(id)}/location`, token, { method: "PUT", body: JSON.stringify(change) });
export const changeLead = (token: string, id: string, change: LeadChange) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify(change) });
export const addLeadNote = (token: string, id: string, entry: LeadNoteEntry) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(id)}/notes`, token, { method: "POST", body: JSON.stringify(entry) });

export interface FollowUp {
  id: string;
  lead_id: string;
  due_on: string;
  due_at: string | null;
  note: string;
  created_at: string;
  done_at: string | null;
  done_email: string | null;
  lead_name: string;
  stage: Stage;
  owner_id: string | null;
  owner_email: string | null;
  missing: Missing[];
  can_edit: boolean;
}
export interface FollowUpList {
  follow_ups: FollowUp[];
  today: string;
  can_own: boolean;
}
export interface FollowUpEntry {
  id: string;
  due_on: string;
  due_at: string | null;
  note: string;
}
export const followUps = (token: string, view: FollowUpView, due: FollowUpDue) =>
  get<FollowUpList>(`/follow-ups?${new URLSearchParams({ view, due })}`, token);
export const addFollowUp = (token: string, leadId: string, entry: FollowUpEntry) =>
  call<LeadDetail>(`/leads/${encodeURIComponent(leadId)}/follow-ups`, token, { method: "POST", body: JSON.stringify(entry) });
export const finishFollowUp = (token: string, id: string) =>
  call<{ follow_up: FollowUp }>(`/follow-ups/${encodeURIComponent(id)}/done`, token, { method: "POST" });

export interface ImportRow {
  line: number;
  fields: { name: string; address: string; phone: string; qualifier: string; previous_collector: string; owner: string; competitor_onsite: boolean };
  owner_id: string | null;
  errors: string[];
  repeats: number[];
  matches: { kind: "lead" | "account"; id: string; name: string; number: string | null }[];
}
export interface ImportPreview {
  rows: ImportRow[];
  ignored: string[];
  bad: number;
  sha256: string;
}
export const previewLeadImport = (token: string, csv: string) =>
  call<ImportPreview>("/leads/import/preview", token, { method: "POST", body: JSON.stringify({ csv }) });
export const importLeads = (token: string, id: string, csv: string) =>
  call<{ imported: number; sha256: string }>("/leads/import", token, { method: "POST", body: JSON.stringify({ id, csv }) });

export interface DealChange {
  site_name?: string;
  address?: string;
  state?: string;
  phone?: string;
  hours?: string;
  contact_name?: string;
  contact_role?: string;
  contact_phone?: string;
  contact_email?: string;
  payee_name?: string;
  payee_address?: string;
  fryers?: number | null;
  changes_per_week?: number | null;
  fryer_lb?: number | null;
  oil_type?: string | null;
  grill_share?: number | null;
  rate_gal?: number | null;
  container_size?: number | null;
  accessories?: string[];
  rebate_method?: string | null;
  rebate_value?: number | null;
  consideration?: string;
  consideration_amount?: number;
  term_months?: number | null;
  auto_renew?: boolean;
  notice_days?: number;
  previous_collector?: string;
  signer_name?: string;
  signer_title?: string;
  signed_on?: string | null;
  delivery_on?: string | null;
  service_start?: string | null;
  area_id?: string | null;
  week_group?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  access_note?: string;
}
export interface Deal extends Required<DealChange> {
  id: string;
  lead_id: string;
  lead_name: string;
  lead_stage: Stage;
  owner_id: string | null;
  owner_email: string | null;
  status: DealStatus;
  approval_reasons: string[];
  reasons_now: string[];
  submitted_email: string | null;
  submitted_at: string | null;
  decided_email: string | null;
  decided_at: string | null;
  decision_note: string;
  account_id: string | null;
  account_number: string | null;
  created_at: string;
  updated_at: string;
  missing: Record<Step, string[]>;
  unsigned: string[];
  can_edit: boolean;
  can_edit_terms: boolean;
  can_submit: boolean;
  can_approve: boolean;
  can_win: boolean;
}
export interface DealDetail {
  deal: Deal;
  container_sizes: number[];
  container_margin: number;
  limits: DealLimits;
  states: string[];
}
export interface DealList {
  deals: Deal[];
  more: boolean;
  can_approve: boolean;
}
export type DealStepKind = "submit" | "approve" | "reject" | "win";
export const deals = (token: string, view: DealView) => get<DealList>(`/deals?${new URLSearchParams({ view })}`, token);
export const deal = (token: string, id: string) => get<DealDetail>(`/deals/${encodeURIComponent(id)}`, token);
export const startDeal = (token: string, leadId: string, id: string) =>
  call<DealDetail>(`/leads/${encodeURIComponent(leadId)}/deal`, token, { method: "POST", body: JSON.stringify({ id }) });
export const changeDeal = (token: string, id: string, change: DealChange) =>
  call<DealDetail>(`/deals/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify(change) });
export const stepDeal = (token: string, id: string, step: DealStepKind, note = "") =>
  call<DealDetail>(`/deals/${encodeURIComponent(id)}/events`, token, { method: "POST", body: JSON.stringify({ step, note }) });

export interface DealLimits {
  rate_max: number;
  percent_max: number;
  flat_max: number;
  consideration_max: number;
}
export interface NumberRange {
  state: string;
  first: number;
  last: number;
  next?: number | null;
}
export interface SalesSettings {
  ranges: NumberRange[];
  limits: DealLimits;
  defaults: DealLimits;
  can_edit: boolean;
}
export const salesSettings = (token: string) => get<SalesSettings>("/settings/sales", token);
export const setSalesSettings = (token: string, body: { ranges: NumberRange[]; limits: DealLimits }) =>
  call<SalesSettings>("/settings/sales", token, { method: "PUT", body: JSON.stringify(body) });

export interface ContractChange {
  id: string;
  starts_on: string;
  renew?: boolean;
  rebate_method?: string;
  rebate_value?: number;
  consideration?: string;
  consideration_amount?: number;
  term_months?: number;
  auto_renew?: boolean;
  notice_days?: number;
  payee_name?: string;
  payee_address?: string;
}
export const changeContract = (token: string, id: string, change: ContractChange) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(id)}/contract/versions`, token, { method: "POST", body: JSON.stringify(change) }));

export interface CheckInEntry {
  id: string;
  met_on: string;
  contact: string;
  rating: number;
  result: string;
  next_on: string | null;
  rebate: { rebate_method: string; rebate_value: number } | null;
}
export const addCheckIn = (token: string, id: string, entry: CheckInEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(id)}/check-ins`, token, { method: "POST", body: JSON.stringify(entry) }));

export interface DocumentEntry {
  id: string;
  kind: DocumentKind;
  file_name: string;
  size_bytes: number;
  sha256: string;
}
export interface UploadTicket {
  id: string;
  url: string;
  headers: Record<string, string>;
}
export const startDocument = (token: string, accountId: string, entry: DocumentEntry) =>
  call<UploadTicket>(`/accounts/${encodeURIComponent(accountId)}/documents`, token, { method: "POST", body: JSON.stringify(entry) });
export const documentStored = (token: string, id: string) =>
  kept(call<AccountDetail>(`/documents/${encodeURIComponent(id)}/stored`, token, { method: "POST" }));
export const documentUrl = async (token: string, id: string) =>
  (await get<{ url: string }>(`/documents/${encodeURIComponent(id)}/download`, token)).url;

export type PhotoTag = "stop_proof" | "condition" | "access";
export type PhotoType = "image/jpeg" | "image/png" | "image/webp";
export interface PhotoEntry {
  id: string;
  tag: PhotoTag;
  content_type: PhotoType;
  size_bytes: number;
  sha256: string;
  taken_at: string;
  route_stop_id?: string | null;
  lat?: number | null;
  lon?: number | null;
}
export interface Photo {
  id: string;
  route_stop_id: string | null;
  tag: PhotoTag;
  content_type: PhotoType;
  size_bytes: number;
  taken_at: string;
  lat: number | null;
  lon: number | null;
  stored: boolean;
  created_at: string;
  stored_at: string | null;
  uploaded_email: string | null;
  url?: string;
}
export const startPhoto = (token: string, accountId: string, entry: PhotoEntry) =>
  call<UploadTicket>(`/accounts/${encodeURIComponent(accountId)}/photos`, token, { method: "POST", body: JSON.stringify(entry) });
export const photoStored = (token: string, id: string) =>
  call<{ photo: Photo }>(`/photos/${encodeURIComponent(id)}/stored`, token, { method: "POST" });
export const accountPhotos = (token: string, accountId: string) =>
  get<{ photos: Photo[]; can_add: boolean }>(`/accounts/${encodeURIComponent(accountId)}/photos`, token);

/** The signed URL takes the file straight to the bucket; the API never holds the bytes. */
export async function uploadFile(ticket: UploadTicket, file: Blob): Promise<void> {
  const response = await fetch(ticket.url, { method: "PUT", headers: ticket.headers, body: file });
  if (!response.ok) throw new ApiError(response.status, "The file was not stored. Try again.");
}

export const renewals = (token: string) => get<RenewalList>("/renewals", token);

export interface PayoutRun {
  id: string;
  tag: string;
  period_type: "quarterly" | "monthly" | "4-4-5" | "custom";
  period_year: number | null;
  period_number: number | null;
  period: string;
  starts_on: string;
  ends_on: string;
  market_value: number | null;
  minimum: number;
  status: "draft" | "approved" | "exported" | "paid";
  created_email: string | null;
  created_at: string;
  approved_email: string | null;
  approved_at: string | null;
  exported_at: string | null;
  lines: number;
  held: number;
  total: number;
}

export interface PayoutList {
  runs: PayoutRun[];
  minimum: number;
  can_add: boolean;
  can_set_minimum: boolean;
  period_types: string[];
  today: string;
}

export interface PayoutLine {
  id: string;
  run_id: string;
  account_id: string;
  number: string;
  name: string;
  contract_version_id: string | null;
  contract_version: number | null;
  payee_name: string;
  payee_address: string;
  first_on: string | null;
  last_on: string | null;
  pickups: number;
  net_gal: number;
  method: "rate" | "percent" | "flat" | null;
  rate: number | null;
  amount: number;
  adjustment: number;
  final_amount: number;
  hold_reason: string;
  payment_status: "due" | "paid" | "failed" | "held";
  hand_delivered: boolean;
  paid_on: string | null;
  carried_from: string | null;
  carried_from_tag: string | null;
  carried_to: string | null;
  carried_to_tag: string | null;
  /** The run rules set the hold, so staff cannot clear it. */
  rule_hold?: boolean;
}

export interface PayoutDetail {
  run: PayoutRun;
  lines: PayoutLine[];
  /** One check for each account and payee. */
  checks?: number;
  can_edit_lines: boolean;
  can_approve: boolean;
  can_export: boolean;
  can_pay: boolean;
  /** Why the draft cannot be approved: an approved run pays some of its days. */
  paid_by_other?: string | null;
}

/** A line of the account in a run that is approved or later. Only contract roles get it. */
export interface AccountPayout {
  id: string;
  run_id: string;
  tag: string;
  starts_on: string;
  ends_on: string;
  status: PayoutRun["status"];
  net_gal: number;
  method: PayoutLine["method"];
  rate: number | null;
  final_amount: number;
  hold_reason: string;
  payment_status: PayoutLine["payment_status"];
  hand_delivered: boolean;
  paid_on: string | null;
}

export interface RunEntry {
  id: string;
  tag: string;
  period_type: PayoutRun["period_type"];
  year?: number;
  number?: number;
  starts_on?: string;
  ends_on?: string;
  market_value?: number | null;
}

export interface LineChange {
  adjustment?: number;
  hold_reason?: string;
  payment_status?: PayoutLine["payment_status"];
  paid_on?: string;
  hand_delivered?: boolean;
}

const runPath = (id: string) => `/payouts/${encodeURIComponent(id)}`;

export const payouts = (token: string) => get<PayoutList>("/payouts", token);
export const payoutRun = (token: string, id: string) => get<PayoutDetail>(runPath(id), token);
export const makePayoutRun = (token: string, entry: RunEntry) =>
  call<PayoutDetail>("/payouts", token, { method: "POST", body: JSON.stringify(entry) });
export const approvePayoutRun = (token: string, id: string) => call<PayoutDetail>(`${runPath(id)}/approve`, token, { method: "POST" });
export const exportPayoutRun = (token: string, id: string, kind: "quickbooks" | "checks") =>
  call<{ file_name: string; csv: string; detail: PayoutDetail }>(`${runPath(id)}/export`, token, { method: "POST", body: JSON.stringify({ kind }) });
export const changePayoutLine = (token: string, id: string, lineId: string, change: LineChange) =>
  call<PayoutDetail>(`${runPath(id)}/lines/${encodeURIComponent(lineId)}`, token, { method: "PATCH", body: JSON.stringify(change) });
export const setPayoutMinimum = (token: string, minimum: number) =>
  call<{ minimum: number }>("/settings/payout", token, { method: "PUT", body: JSON.stringify({ minimum }) });

export interface RoleGrant {
  role: string;
  scope: "tenant" | "hub";
  hub_ids: string[];
}
export interface Hub {
  id: string;
  name: string;
}
export interface Member {
  id: string;
  email: string;
  name: string;
  status: "active" | "deactivated";
  created_at: string;
  deactivated_at: string | null;
  roles: RoleGrant[];
  last_sign_in: string | null;
  me: boolean;
  /** Rule V-11: only a member who can run a route has a preferred truck. */
  can_drive: boolean;
  preferred_truck_id: string | null;
  preferred_truck: string | null;
}
export interface MemberTruck {
  id: string;
  code: string;
  active: boolean;
}
export interface MemberList {
  members: Member[];
  hubs: Hub[];
  trucks: MemberTruck[];
  known_roles: string[];
  role_names: Record<string, string>;
}
export type MemberDetail = Member & { hubs: Hub[]; trucks: MemberTruck[]; known_roles: string[]; role_names: Record<string, string> };
const memberPath = (id: string) => `/members/${encodeURIComponent(id)}`;
export const members = (token: string) => get<MemberList>("/members", token);
export const member = (token: string, id: string) => get<MemberDetail>(memberPath(id), token);
export const inviteMember = (token: string, entry: { email: string; name: string; roles: RoleGrant[] }) =>
  call<Member & { mail_sent: boolean }>("/members", token, { method: "POST", body: JSON.stringify(entry) });
export const changeMember = (token: string, id: string, change: { name?: string; roles?: RoleGrant[]; preferred_truck_id?: string | null }) =>
  call<Member>(memberPath(id), token, { method: "PATCH", body: JSON.stringify(change) });
export const deactivateMember = (token: string, id: string) =>
  call<Member & { sign_in_disabled: boolean }>(`${memberPath(id)}/deactivate`, token, { method: "POST" });
export const reactivateMember = (token: string, id: string, roles: RoleGrant[]) =>
  call<Member & { sign_in_enabled: boolean }>(`${memberPath(id)}/reactivate`, token, { method: "POST", body: JSON.stringify({ roles }) });

export type Letters = Record<string, string>;
export interface BuiltInRole {
  key: string;
  letters: Letters;
}
export interface CustomRole {
  id: string;
  key: string;
  name: string;
  based_on: string;
  letters: Letters;
  assigned: number;
  created_at: string;
}
export interface RoleList {
  resources: string[];
  built_in: BuiltInRole[];
  custom: CustomRole[];
}
export type RoleDetail = CustomRole & { resources: string[]; base_letters: Letters };
const rolePath = (id: string) => `/roles/${encodeURIComponent(id)}`;
export const roles = (token: string) => get<RoleList>("/roles", token);
export const role = (token: string, id: string) => get<RoleDetail>(rolePath(id), token);
export const createRole = (token: string, entry: { name: string; based_on: string; letters: Letters }) =>
  call<CustomRole>("/roles", token, { method: "POST", body: JSON.stringify(entry) });
export const changeRole = (token: string, id: string, change: { name?: string; letters?: Letters }) =>
  call<CustomRole>(rolePath(id), token, { method: "PATCH", body: JSON.stringify(change) });
export const deleteRole = (token: string, id: string) => call<{ deleted: string }>(rolePath(id), token, { method: "DELETE" });

export interface ApiKey {
  id: string;
  kid: string;
  label: string;
  scopes: string[];
  hub_ids: string[];
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  last_used_at: string | null;
  status: "active" | "revoked" | "expired";
}
export const apiKeys = (token: string) => get<{ keys: ApiKey[]; hubs: Hub[] }>("/keys", token);
export const createKey = (token: string, entry: { label: string; hub_ids: string[]; expires_on: string | null }) =>
  call<ApiKey & { key: string }>("/keys", token, { method: "POST", body: JSON.stringify(entry) });
export const revokeKey = (token: string, id: string) => call<ApiKey>(`/keys/${encodeURIComponent(id)}/revoke`, token, { method: "POST" });

export type ImportState = "ok" | "refused" | "failed";

/** The last hourly check of the sheet. An older API does not send it. */
export interface ImportCheck {
  at: string;
  state: ImportState | "unchanged";
  run_id: string | null;
  run_at: string | null;
}

export interface ImportRun {
  id: string;
  started_at: string;
  finished_at: string;
  mode: "dry-run" | "apply";
  state: ImportState;
  actor: string;
  fixture: boolean;
  counts: Record<string, number>;
  refused_reason: string;
  issues: number;
}

export interface ImportIssue {
  seq: number;
  tab: string;
  row: number | null;
  column: string;
  code: string;
  number: string | null;
  value: string | null;
  account_id: string | null;
}

export const importRuns = (token: string) => get<{ runs: ImportRun[]; last_check?: ImportCheck | null }>("/import/runs", token);
export const importIssues = (token: string, id: string) =>
  get<{ run: ImportRun; issues: ImportIssue[] }>(`/import/runs/${encodeURIComponent(id)}/issues`, token);

export type SourceMode = "off" | "report" | "apply";

/** Design 26.11 F8: one import source of the organization. An operator sets the adapter and the settings. */
export interface ImportSource {
  kind: "service" | "sales";
  adapter: string;
  label: string;
  settings: Record<string, string | number | boolean>;
  mode: SourceMode;
  paused_at: string | null;
  paused_by: string | null;
  paused_by_name?: string | null;
  updated_at: string;
  updated_by: string;
}

export interface ImportSources {
  sources: ImportSource[];
  can_edit: boolean;
  can_run: boolean;
  modes?: SourceMode[];
}

export interface SourceAccess {
  kind: "service" | "sales";
  ok: boolean;
  code: string;
  detail: Record<string, number>;
}

/** An older API has no source list: it answers 404, and the page shows only the runs. */
export const importSources = (token: string) => get<ImportSources>("/import/sources", token);
export const setSourceMode = (token: string, kind: ImportSource["kind"], mode: SourceMode) =>
  call<ImportSources>(`/import/sources/${kind}/mode`, token, { method: "PUT", body: JSON.stringify({ mode }) });
export const pauseImport = (token: string, paused: boolean) =>
  call<ImportSources>("/import/pause", token, { method: "PUT", body: JSON.stringify({ paused }) });
export const testImportAccess = (token: string) => call<{ access: SourceAccess[] }>("/import/test", token, { method: "POST" });
export const runImport = (token: string, mode: "dry-run" | "apply") =>
  call<{ started: boolean; mode: string }>("/import/run", token, { method: "POST", body: JSON.stringify({ mode }) });

/** A skipped visit comes only from a change of a stop to Skip after a visit (rule D-18). */
export type VisitKind = "pickup" | "level" | "no_access" | "skipped";
export type VisitReason = "not_full" | "locked" | "no_access" | "blocked" | "other";

/** One tank visit as the browser sends it. The id is made in the browser, so a retry is safe. */
export interface VisitEntry {
  id: string;
  account_id: string;
  at: string;
  kind: VisitKind;
  raw_gal?: number;
  scrap_gal?: number;
  gallons_left?: number;
  reason?: VisitReason;
  /** Rule V-19: an older API does not keep them, and it ignores them. */
  note?: string;
  labels?: string[];
  driver_id?: string;
  truck_id?: string;
  route_stop_id?: string;
  load_id?: string;
  old_confirmed?: boolean;
  place?: Place;
}

export interface Visit {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  at: string;
  kind: VisitKind;
  raw_gal: number | null;
  scrap_gal: number | null;
  gallons_left: number | null;
  reason: VisitReason | null;
  note?: string;
  /** The label codes and their texts; an older API sends neither. */
  labels?: string[];
  label_text?: string[];
  driver_id: string | null;
  driver_name: string | null;
  truck_id: string | null;
  truck_name: string | null;
  source: string;
  corrected: boolean;
  can_correct?: boolean;
  place?: Place | null;
}

export interface VisitChoice {
  id: string;
  name: string;
}

export interface VisitDefaults {
  driver_id: string | null;
  truck_id: string | null;
  next_stop: { account_id: string; route_stop_id: string } | null;
  pre_trip_needed?: boolean;
  unit: string;
  offline_hours?: number;
  place_far_m?: number;
  /** An older API does not send it: the check is on. */
  place_check_on?: boolean;
  drivers?: VisitChoice[];
  /** `capacity_gal` is absent from an older API. */
  trucks?: { id: string; code: string; capacity_gal?: number }[];
}

export interface NearbyAccount {
  id: string;
  number: string;
  name: string;
  address: string;
  distance_m: number;
}

export interface BatchResult {
  saved: number;
  unchanged: number;
  errors: { id: string; code: string; detail: string }[];
}

export interface CompareWeek {
  week_end: string;
  replaced_same: number;
  replaced_diff: number;
  level_only_with_sheet: number;
  app_only: number;
}

export interface CompareRow {
  account_id: string;
  account_number: string;
  account_name: string;
  week_end: string;
  sheet_raw: number | null;
  app_raw: number | null;
  case: string;
}

export const recordVisit = (token: string, entry: VisitEntry) =>
  call<{ visit: Visit }>("/visits", token, { method: "POST", body: JSON.stringify(entry) });
export const recordVisits = (token: string, visits: readonly VisitEntry[]) =>
  call<BatchResult>("/visits/batch", token, { method: "POST", body: JSON.stringify({ visits }) });
export const correctVisit = (token: string, id: string, entry: VisitEntry) =>
  call<{ visit: Visit }>(`/visits/${encodeURIComponent(id)}/correct`, token, { method: "POST", body: JSON.stringify(entry) });
export function visits(token: string, filter: { day?: string; driver?: string; account?: string }) {
  const params = new URLSearchParams(Object.entries(filter).filter((pair): pair is [string, string] => Boolean(pair[1])));
  return get<{ visits: Visit[] }>(`/visits?${params}`, token);
}
export const visitDefaults = (token: string) => get<VisitDefaults>("/visits/defaults", token);

export interface VisitLabel {
  code: string;
  text: string;
  active: boolean;
}

/** Rule V-19: the labels of the organization in their order. The first `chips` active ones show as chips. */
export interface VisitLabels {
  labels: VisitLabel[];
  chips?: number;
  text_max?: number;
  can_edit?: boolean;
}

export type VisitLabelChange = { code?: string; text: string; active: boolean };

/** An older API answers 404: Record a visit then shows only the note box. */
export const visitLabels = (token: string) => get<VisitLabels>("/visit-labels", token);
export const setVisitLabels = (token: string, labels: readonly VisitLabelChange[]) =>
  call<VisitLabels>("/visit-labels", token, { method: "PUT", body: JSON.stringify({ labels }) });
export const nearbyAccounts = (token: string, lat: number, lng: number, limit = 10) =>
  get<{ accounts: NearbyAccount[] }>(`/visits/nearby?lat=${lat}&lng=${lng}&limit=${limit}`, token);
export const accountVisits = (token: string, id: string) => get<{ visits: Visit[] }>(`/accounts/${encodeURIComponent(id)}/visits`, token);
export const sheetCompare = (token: string, weeks = 8) =>
  get<{ weeks: CompareWeek[]; rows?: CompareRow[] | null }>(`/sheet-import/compare?weeks=${weeks}`, token);

export type ParityReason = "sheet_wrong" | "app_wrong" | "timing" | "new_account" | "other";
export type ParityFlag = "high_risk" | "overflow" | "due";

export interface ParitySide {
  rate: number | null;
  weeks_to_full: number | null;
  load: number | null;
  flags: ParityFlag[];
}

export interface ParityNote {
  reason: ParityReason;
  note: string;
  by: string | null;
  at: string;
}

export interface ParityRow {
  account_id: string;
  number: string;
  name: string;
  status: string;
  area: string | null;
  on_app?: boolean;
  data_source?: DataSource;
  capacity_gal: number;
  sheet: ParitySide;
  app: ParitySide;
  differences: string[];
  agrees: boolean;
  note: ParityNote | null;
}

export interface ParitySummary {
  rows: number;
  agree: number;
  share: number | null;
  gate: number;
  tolerance: number;
  meets_gate: boolean;
  unexplained: number;
  /** The cycles in a row of the gate (roadmap 086). An older API does not send it. */
  cycles?: number;
}

export interface Parity {
  cycle_start: string;
  summary: ParitySummary;
  rows: ParityRow[];
  can_note: boolean;
  reasons: ParityReason[];
  app_areas?: { code: string; app_from: string }[];
}

export const parity = (token: string) => get<Parity>("/parity", token);

/** The values that one side of a parity row comes from (roadmap 101). */
export interface ParityInputs {
  restart: string | null;
  newest: string | null;
  /** The raw gallons of the four 13-week periods, the newest first. */
  periods: number[];
  stated_rate: number | null;
  rate_used: number | null;
  weeks_left: number | null;
  next_visit: string | null;
  projected_gal: number | null;
  projected_share: number | null;
  early_pull: boolean | null;
  early_by: "share" | "estimate";
  off_season: boolean | null;
}

export interface ParityPickup {
  id: string;
  at: string;
  raw_gal: number;
  scrap_gal: number;
  source: DayPickup["source"];
  /** False for a pickup before the restart day: neither side counts it. */
  counted: boolean;
  on: "both" | "app" | "sheet";
}

export interface ParityDetail {
  row: ParityRow;
  window: { start: string; end: string };
  inputs: { app: ParityInputs; sheet: ParityInputs };
  pickups: ParityPickup[];
  only: { app: { count: number; gal: number }; sheet: { count: number; gal: number } };
  cut: boolean;
}

export const parityDetail = (token: string, accountId: string) => get<ParityDetail>(`/parity/${accountId}`, token);
export const parityNotes = (token: string, accountIds: readonly string[], reason: ParityReason, note: string) =>
  call<{ saved: number; cycle_start: string }>("/parity/notes/bulk", token, {
    method: "POST",
    body: JSON.stringify({ account_ids: accountIds, reason, note }),
  });

/** OQ-O19: the gate numbers of the organization. The share and the tolerance are fractions. */
export interface ParityGate {
  cycles: number;
  share: number;
  tolerance: number;
}

export interface ParityCycle {
  cycle_start: string;
  day: string;
  total: number;
  agree: number;
  unexplained: number;
  share: number | null;
  met: boolean;
}

export interface ParityPoint {
  day: string;
  total: number;
  agree: number;
  unexplained: number;
  share: number | null;
}

/** The counts of each day of `ParityHistory.days`; null on a day with no row. */
export interface ParitySeries {
  total: (number | null)[];
  agree: (number | null)[];
  unexplained: (number | null)[];
  cycles: ParityCycle[];
  streak: number;
  meets: boolean;
  latest: ParityPoint | null;
}

export interface ParityHistory {
  days: string[];
  organization: ParitySeries;
  areas: (ParitySeries & { area: string })[];
  gate: ParityGate;
  defaults: ParityGate;
  can_edit: boolean;
  cycle_start: string;
  cycles: string[];
}

export interface ParityCycleNote {
  account_id: string;
  number: string;
  name: string;
  area: string | null;
  reason: ParityReason;
  note: string;
  by: string | null;
  at: string;
}

export type ReadinessKey = "parity" | "import" | "accounts" | "drivers" | "truck" | "routes";

export interface ReadinessAccepted {
  reason: string;
  by: string | null;
  at: string;
}

export interface ReadinessCheck {
  key: ReadinessKey;
  passed: boolean;
  ok: boolean;
  done: number | null;
  of: number | null;
  share: number | null;
  accepted: ReadinessAccepted | null;
  streak?: number;
}

export interface ReadinessArea {
  area: string;
  accounts: number;
  on_app: boolean;
  checks: ReadinessCheck[];
  ready: boolean;
  ready_share: number;
}

export interface Readiness {
  areas: ReadinessArea[];
  import_run: string | null;
  checks: ReadinessKey[];
  gate: ParityGate;
  can_accept: boolean;
}

export interface ReadinessRow {
  account_id?: string;
  member_id?: string;
  number?: string;
  name?: string;
  code?: string;
  tab?: string;
  row?: number | null;
  missing?: string[];
}

export const parityHistory = (token: string) => get<ParityHistory>("/parity/history", token);
export const parityCycleNotes = (token: string, cycle: string) =>
  get<{ cycle_start: string; notes: ParityCycleNote[] }>(`/parity/cycle-notes?cycle=${encodeURIComponent(cycle)}`, token);
export const saveParityGate = (token: string, gate: ParityGate) =>
  call<{ gate: ParityGate; defaults: ParityGate; can_edit: boolean }>("/parity/gate", token, { method: "PUT", body: JSON.stringify(gate) });
export const readiness = (token: string) => get<Readiness>("/parity/readiness", token);
export const readinessRows = (token: string, area: string, check: ReadinessKey) =>
  get<{ area: string; check: ReadinessKey; rows: ReadinessRow[]; limit: number }>(
    `/parity/readiness/rows?area=${encodeURIComponent(area)}&check=${encodeURIComponent(check)}`,
    token,
  );
export const acceptReadiness = (token: string, area: string, check: ReadinessKey, reason: string) =>
  call<{ id: string }>("/parity/readiness/accept", token, { method: "POST", body: JSON.stringify({ area, check, reason }) });
/** Design 26.11 F5 (roadmap 088). An API before 088 gives a 404, and the section does not show. */
export type WatchState = "none" | "open" | "passed" | "failed";
export type WatchReason = "visited_off_app" | "not_needed" | "closed" | "no_access" | "other";

export interface WatchCalls {
  watch: number;
  shadow: number[];
  ok: boolean;
}

export interface WatchArea {
  area_id: string;
  code: string;
  state: WatchState;
  app_from: string | null;
  cycle_start: string | null;
  cycle_end: string | null;
  shadow_starts?: string[];
  lost_open: number;
  day?: number;
  days?: number;
  lost?: { open: number; visit: number; reason: number; total: number };
  gallons?: { app: number; shadow: number[]; estimate: number; low: number | null; high: number | null; in_band: boolean | null };
  overflows?: WatchCalls;
  at_fault?: WatchCalls;
  rebates?: { weeks: number; compared: number; differences: number; app_gal: number; source_gal: number };
  visits?: number;
  corrections?: number;
  green?: { lost: boolean; rebates: boolean; gallons: boolean | null; calls: boolean };
}

export interface CutoverWatch {
  areas: WatchArea[];
  can_edit: boolean;
}

export interface WatchLost {
  account_id: string;
  number: string;
  name: string;
  kind: "due" | "late";
  due_on: string;
  state: "open" | "visit" | "reason";
  visited_week: string | null;
  owner_id: string | null;
  owner: string | null;
  reason: WatchReason | null;
  note: string;
  closed_at: string | null;
  closed_by: string | null;
  /** Rule T-15: a close with a reason can open again. An API before the reopen sends no field. */
  can_reopen?: boolean;
}

export interface WatchRebateRow {
  account_id: string;
  number: string;
  name: string;
  app_gal: number;
  source_gal: number;
  /** The weeks that the rebate compares. An older API sends no weeks. */
  weeks?: string[];
}

export interface WatchDetail extends WatchArea {
  weeks: { week_start: string; app: number | null; shadow: number[] }[];
  lost_rows: WatchLost[];
  rebate_rows: WatchRebateRow[];
  people: { day: string; member_id: string | null; name: string | null; visits: number; corrections: number }[];
  owners: { id: string; name: string }[];
  default_owner: string | null;
  reasons: WatchReason[];
  limit: number;
  can_edit: boolean;
}

export interface WatchLostAction {
  area_id: string;
  account_id: string;
  kind: "due" | "late";
  due_on: string;
  action: "owner" | "close" | "reopen";
  owner_id?: string;
  reason?: WatchReason;
  note?: string;
}

export const cutoverWatch = (token: string) => get<CutoverWatch>("/cutover/watch", token);
export const cutoverWatchArea = (token: string, areaId: string) => get<WatchDetail>(`/cutover/watch/${encodeURIComponent(areaId)}`, token);
export const watchLostAction = (token: string, body: WatchLostAction) =>
  call<{ id: string; action?: string }>("/cutover/watch/lost", token, { method: "POST", body: JSON.stringify(body) });

/** Design 26.11 F1: the steps of an area, in order. */
export type CutoverStep = "sheet" | "shadow" | "ready" | "scheduled" | "app" | "watch_passed";
export type CutoverOrgStep = "T0" | "T1" | "T2" | "T3";
export type CutoverAction = "add_area" | "import" | "readiness" | "schedule" | "wait_switch" | "watch" | "finish" | "done";

/** Roadmap 088: the watch of the first cycle on the app. An API before 088 sends null. */
export interface CutoverAreaWatch {
  area_id: string;
  code: string;
  state: "none" | "open" | "passed" | "failed";
  cycle_start?: string | null;
  cycle_end?: string | null;
  lost_open?: number;
}

export interface CutoverArea {
  id: string;
  code: string;
  region: string;
  rotation_week: number;
  pickup_source: PickupSource;
  app_from: string | null;
  switch_state: DataSourceState;
  step: CutoverStep;
  accounts: number;
  parity: ParitySeries | null;
  readiness: Pick<ReadinessArea, "checks" | "ready" | "ready_share"> | null;
  watch?: CutoverAreaWatch | null;
  can_switch: boolean;
}

export interface CutoverHeader {
  step: CutoverOrgStep;
  areas: number;
  on_app: number;
  steps: Record<CutoverStep, number>;
  sources_on: number;
  next: { action: CutoverAction; area: string | null; day: string | null };
  parity: ParitySeries;
}

export interface Cutover {
  /** Null when the page comes from the sales areas of an API before roadmap 085. */
  header: CutoverHeader | null;
  areas: CutoverArea[];
  days: string[];
  import_run: string | null;
  gate: ParityGate;
  checks: ReadinessKey[];
  cycle_start: string;
  can_accept: boolean;
  can_parity: boolean;
  can_export: boolean;
  /** Roadmap 092. An older API sends neither: then no button and no Finished state. */
  can_finish?: boolean;
  finished?: CutoverFinished | null;
  /** SO-4232: the finish in the admin job that runs or failed. An older API sends none. */
  finish?: CutoverFinishState | null;
}

export interface CutoverFinishState {
  id: string | null;
  state: "running" | "failed";
  /** The start of a run, else the time of the failure. */
  at: string;
  by: string | null;
  by_name?: string | null;
  step?: string | null;
  code?: string | null;
  started_at?: string | null;
}

/** A newer API answers 202 with the state; an older API answers 200 when the finish is done. */
export type CutoverFinishReply = Partial<CutoverFinishState> & {
  finished?: CutoverFinished | null;
  run_state?: string | null;
  sources?: string[];
};

/** Design 26.11 F9: when and by whom the cutover finished. */
export interface CutoverFinished {
  at: string;
  by: string;
  by_name?: string | null;
  run_id: string | null;
  export: string | null;
  sources: string[];
}

export const cutover = (token: string) => get<Cutover>("/cutover", token);
export const cutoverFinish = (token: string) =>
  call<CutoverFinishReply>("/cutover/finish", token, { method: "POST" });
export const cutoverFinishExport = (token: string) => get<{ url: string; file: string }>("/cutover/finish/export", token);
export type RequestStatus = "received" | "scheduled" | "collected" | "cancelled" | "late";

export interface PickupRequest {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  account_deleted: boolean;
  requested_at: string;
  requested_on: string;
  requested_by: string | null;
  need_by: string;
  note: string;
  source: "screen" | "call" | "seed" | "portal";
  call_id: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  scheduled_on: string | null;
  status: RequestStatus;
  can_cancel: boolean;
  /** WF-23: an older API leaves these out; then the request is a pickup at any time. */
  kind?: RequestKind;
  time_window?: "any" | "morning" | "afternoon";
  photo_id?: string | null;
}

export type RequestKind = "pickup" | "container" | "lock" | "other";

export interface RequestEntry {
  id: string;
  need_by?: string;
  note: string;
}

export type RequestFilter = "open" | "all" | RequestStatus;

export const pickupRequests = (token: string, status: RequestFilter) =>
  get<{ requests: PickupRequest[]; limit: number; can_cancel: boolean }>(`/pickup-requests?status=${status}`, token);
export const requestPickup = (token: string, accountId: string, entry: RequestEntry) =>
  kept(call<AccountDetail>(`/accounts/${encodeURIComponent(accountId)}/pickup-requests`, token, {
    method: "POST",
    body: JSON.stringify(entry),
  }));
export const requestPhoto = (token: string, id: string) => get<{ url: string }>(`/pickup-requests/${encodeURIComponent(id)}/photo`, token);
export const cancelRequests = (token: string, ids: readonly string[]) =>
  call<{ cancelled: number; unchanged: number }>("/pickup-requests/cancel", token, { method: "POST", body: JSON.stringify({ ids }) });

/** SO-1462: a message of a portal user, on the account of its Location. */
export interface CustomerMessage {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  location: string | null;
  sent_at: string;
  text: string;
  status: "open" | "done";
  done_at: string | null;
  done_by: string | null;
  can_edit: boolean;
  /** SO-1473: the email of the portal user who wrote it. An older API does not send it. */
  from_email?: string | null;
}

export type MessageFilter = "open" | "done" | "all";

export const customerMessages = (token: string, status: MessageFilter) =>
  get<{ messages: CustomerMessage[]; limit: number; can_edit: boolean }>(`/portal-messages?status=${status}`, token);
export const setMessageStatus = (token: string, ids: readonly string[], status: "open" | "done") =>
  call<{ changed: number; unchanged: number }>("/portal-messages/status", token, { method: "POST", body: JSON.stringify({ ids, status }) });

export type OverviewState = "done" | "checked" | "skipped" | "late" | "open";
export type LookCode = "quiet_route" | "late_stop" | "overflow" | "sensor_alert";

export interface OverviewRoute {
  id: string;
  colour: number;
  truck: string;
  driver_email: string | null;
  status: "planned" | "started" | "done" | "cancelled";
  stops: number;
  states: Record<OverviewState, number>;
  gallons: number;
  planned_gal: number;
  last_report_at: string | null;
  quiet: boolean;
  last_stop: { lat: number; lng: number; number: string; at: string } | null;
}

export interface OverviewPoint extends LatLng {
  route_id: string;
  position: number;
  state: OverviewState;
  account_id: string;
  number: string;
  name: string;
  arrive: string | null;
  at: string | null;
  /** The median estimated fill share of today. Absent from an older API. */
  fill?: number | null;
}

export interface OverviewWeek {
  week_start: string;
  gallons: number;
  before_gal: number;
}

export interface Overview {
  day: string;
  now: string;
  working: boolean;
  rules: { day_start: string; day_end: string; quiet_h: number; late_min: number; alert_h: number; stale_h: number };
  looks: { code: LookCode; count: number; names: string[] }[];
  stops: Record<OverviewState, number> & { planned: number; no_point: number };
  routes: OverviewRoute[];
  points: OverviewPoint[];
  gallons: {
    today: number;
    today_planned: number;
    /** An older API does not send it. */
    today_stops?: number;
    week: number;
    week_before: number;
    week_start: string;
    days: number[];
    days_before: number[];
    weeks: OverviewWeek[];
  };
  issues: { open: number; overdue: number; alerts: number; new_alerts: number; ages: number[]; alert_rules: Record<string, number> };
  tanks: { in_field: number; full: number; stale: number; points: (LatLng & { label: string; account_id: string; number: string; state: "full" | "alarm"; fill?: number | null; float_pct?: number | null; name?: string | null; level?: string | null })[] };
  overflows: { at: string; account_id: string; number: string }[];
  timing_ms: Record<string, number>;
}

export interface OverviewLog {
  numbers_ms?: number;
  map_ms?: number;
  failed?: ("numbers" | "map" | "areas")[];
  status?: number;
}

export const overview = (token: string, signal?: AbortSignal) => get<Overview>("/overview", token, signal);

/** SO-1038: one pickup that the gallons tile of the Overview sums. */
export interface DayPickup {
  id: string;
  account_id: string;
  number: string;
  name: string;
  recorded_at: string | null;
  raw_gal: number | null;
  scrap_gal: number | null;
  source: "seed" | "screen" | "import";
  visit_id: string | null;
}
export interface DayPickups {
  day: string;
  count: number;
  raw_gal: number;
  scrap_gal: number;
  limit: number;
  pickups: DayPickup[];
}
export const dayPickups = (token: string, day?: string) => get<DayPickups>(`/pickups${day ? `?day=${day}` : ""}`, token);
export interface SummaryAccount {
  account_id: string;
  number: string;
  name: string;
  gal: number;
  /** The p50 fill of now, as the level dot (roadmap 101). An older API does not send it. */
  fill_share?: number | null;
}
export interface SummaryArea {
  area_id: string | null;
  name: string | null;
  gal: number;
  account_count: number;
  accounts: SummaryAccount[];
  rest: { accounts: number; gal: number };
}
export interface PickupsSummary {
  since: string;
  day: string;
  weeks: { start: string; gal: number }[];
  count: number;
  gal: number;
  accounts: number;
  areas: SummaryArea[];
  accounts_max?: number;
}
/** `all` asks for each account. An older API ignores it and sends an "N more" box for each area. No day: today of the org. */
export const pickupsSummaryOf = (token: string, day: string | undefined, weeks: number, area?: { id: string; max: number }, all = false, signal?: AbortSignal) =>
  get<PickupsSummary>(
    `/pickups/summary?${day ? `day=${day}&` : ""}weeks=${weeks}${area ? `&area_id=${encodeURIComponent(area.id)}&accounts_max=${area.max}` : ""}${all ? "&all_accounts=true" : ""}`,
    token,
    signal,
  );
export const overviewLog = (token: string, entry: OverviewLog) =>
  call<{ status: string }>("/overview/log", token, { method: "POST", body: JSON.stringify(entry), keepalive: true });

export type ExportKind = "accounts" | "pickups" | "pickups-sheet" | "parity";
/** One CSV file of the cutover (design 26.11 F6). The server writes the download into the audit. */
export async function exportFile(token: string, kind: ExportKind): Promise<{ name: string; blob: Blob }> {
  const response = await fetch(`/api/exports/${kind}.csv`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new ApiError(response.status);
  const named = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "");
  return { name: named?.[1] ?? `${kind}.csv`, blob: await response.blob() };
}
export interface ActivityChange {
  field: string;
  before: string;
  after: string;
}
export interface ActivityEvent {
  seq: number;
  at: string;
  actor_type: string;
  actor_id: string;
  actor_email: string | null;
  actor_name: string | null;
  on_behalf_of: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  resource_label: string | null;
  source: string;
  reason: string | null;
  changes: ActivityChange[];
}
export interface ChainCheck {
  checked_at: string;
  events: number;
  problems: number;
  detail: string[];
  ok: boolean;
}
export interface ActivityList {
  events: ActivityEvent[];
  next_before: number | null;
  actions: string[];
  members: { id: string; email: string; name: string }[];
  unknown_account?: boolean;
  chain_check: ChainCheck | null;
}
export interface ActivityFilters {
  action?: string;
  actor?: string;
  account?: string;
  before?: number;
}
export function activityPath(filters: ActivityFilters): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value !== undefined && value !== "") query.set(key, String(value));
  const text = query.toString();
  return `/activity${text ? `?${text}` : ""}`;
}
export const activityList = (token: string, filters: ActivityFilters) => get<ActivityList>(activityPath(filters), token);
